import React, { useState, useEffect, useRef } from 'react';
import { X, Download, Upload, Youtube, Image as ImageIcon, AlignLeft, AlignCenter, AlignRight, ArrowUp, ArrowDown, RotateCcw, Loader2, Sparkles, Save, Columns2, Trash2, Pencil } from 'lucide-react';
import { Language } from '../types';
import { updateYouTubeVideo } from '../services/youtubeService';
import { generateTitleForNode } from '../services/geminiService';

// ── Presets (canonical source — both modals import from here) ─────────────────
export const THUMB_PRESETS = [
    { id: 'amber',  label: 'Amber',  from: '#78350f', to: '#92400e', accent: '#d97706', circle: '#fbbf24' },
    { id: 'blue',   label: 'Blue',   from: '#1e3a8a', to: '#1d4ed8', accent: '#3b82f6', circle: '#60a5fa' },
    { id: 'green',  label: 'Green',  from: '#14532d', to: '#15803d', accent: '#16a34a', circle: '#4ade80' },
    { id: 'purple', label: 'Purple', from: '#4c1d95', to: '#6d28d9', accent: '#8b5cf6', circle: '#c4b5fd' },
    { id: 'red',    label: 'Red',    from: '#7f1d1d', to: '#b91c1c', accent: '#ef4444', circle: '#fca5a5' },
    { id: 'slate',  label: 'Slate',  from: '#0f172a', to: '#1e293b', accent: '#475569', circle: '#94a3b8' },
] as const;
export type ThumbPresetId = typeof THUMB_PRESETS[number]['id'];
// 'title' needs no screenshot at all: the title in a label over a blurred backdrop (the book cover
// when there is one). For notes written from scratch, with nothing captured from a book.
type ThumbStyle = 'split' | 'screenshot' | 'title';
// Styles whose title sits on top of a full-bleed backdrop, sharing the placement/alignment/darkness controls.
const isOverlayStyle = (st: ThumbStyle) => st === 'screenshot' || st === 'title';

interface ColorCombo {
    id: string;
    bg: string;
    accent: string;
    leftBg: string | null;
    titleColor: string;
    subtitleColor: string;
}

// ── Curated font picker ─────────────────────────────────────────────────────────
// Mostly system-safe fonts that need no loading (deterministic export with zero risk
// of a font-not-ready race). "Poppins" is the one real web font, loaded on demand —
// ensureFontReady() must resolve before generateThumbnail() runs with it selected,
// since canvas text rendering does not itself wait for a font load to finish.
export const THUMB_FONTS: { id: string; label: string; family: string; googleFont?: string }[] = [
    { id: 'arial',      label: 'Sans',      family: 'Arial, sans-serif' },
    { id: 'impact',     label: 'Impact',    family: 'Impact, "Arial Narrow", sans-serif' },
    { id: 'georgia',    label: 'Serif',     family: 'Georgia, "Times New Roman", serif' },
    { id: 'mono',       label: 'Mono',      family: '"Courier New", Courier, monospace' },
    { id: 'poppins',    label: 'Poppins',   family: '"Poppins", Arial, sans-serif', googleFont: 'Poppins:wght@800' },
    // Modern additions — Montserrat/Outfit/Space Grotesk are clean geometric sans faces
    // for a contemporary look; Bebas Neue/Anton are the ultra-bold condensed display
    // faces most YouTube thumbnails actually use for punchy title text.
    { id: 'montserrat', label: 'Montserrat', family: '"Montserrat", Arial, sans-serif', googleFont: 'Montserrat:wght@800' },
    { id: 'outfit',     label: 'Outfit',    family: '"Outfit", Arial, sans-serif', googleFont: 'Outfit:wght@700' },
    { id: 'space',      label: 'Grotesk',   family: '"Space Grotesk", Arial, sans-serif', googleFont: 'Space+Grotesk:wght@700' },
    { id: 'bebas',      label: 'Bebas',     family: '"Bebas Neue", Impact, sans-serif', googleFont: 'Bebas+Neue' },
    { id: 'anton',      label: 'Anton',     family: '"Anton", Impact, sans-serif', googleFont: 'Anton' },
];

const loadedGoogleFonts = new Set<string>();
export async function ensureFontReady(fontFamily: string): Promise<void> {
    const font = THUMB_FONTS.find(f => f.family === fontFamily);
    if (font?.googleFont) {
        if (!loadedGoogleFonts.has(font.googleFont)) {
            loadedGoogleFonts.add(font.googleFont);
            const link = document.createElement('link');
            link.rel = 'stylesheet';
            link.href = `https://fonts.googleapis.com/css2?family=${font.googleFont}&display=swap`;
            document.head.appendChild(link);
        }
        // googleFont is either "Family:wght@NNN" or just "Family" (single-weight display
        // faces like Bebas Neue/Anton) — parse both, and turn the URL's "+"-for-space
        // back into a real space for the CSS font-family name document.fonts.load() needs.
        const [rawFamily, weightSpec] = font.googleFont.split(':wght@');
        const weight = weightSpec || '400';
        const familyName = rawFamily.replace(/\+/g, ' ');
        try { await document.fonts.load(`${weight} 48px "${familyName}"`); } catch { /* fall back silently to the stack's next font */ }
    }
    try { await document.fonts.ready; } catch { /* fonts API unavailable — proceed without waiting */ }
}

// ── Text-effect types + pure, deterministic helpers (unit-testable) ────────────
export type TextEffectMode = 'auto' | 'none' | 'custom';
export type TitleAlign = 'left' | 'center' | 'right';
export type TitlePlacement = 'top' | 'center' | 'bottom';
export type FocalPosition =
    | 'top-left' | 'top-center' | 'top-right'
    | 'center-left' | 'center' | 'center-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right';
export type ImageFit = 'fit' | 'fill';
export type CornerRadiusSize = 'none' | 'small' | 'medium' | 'large';

// A simple freehand pen mark, drawn directly on the live (already-composited) preview — points are
// normalized 0-1 relative to the final 1280x720 canvas, not the source image, which keeps the tool
// genuinely simple (no crop/zoom coordinate mapping) at the cost of strokes not following the image
// if the crop/zoom/template is changed afterward — an accepted tradeoff for "just a quick pen mark".
export interface PenStroke { id: string; color: string; points: { x: number; y: number }[]; }
const PEN_COLORS = ['#ef4444', '#f59e0b', '#22c55e', '#3b82f6', '#ffffff'];

export interface ShadowConfig { color: string; opacity: number; blur: number; offsetX: number; offsetY: number; }
export interface OutlineConfig { color: string; width: number; }
export interface TextEffectConfig { mode: TextEffectMode; shadow: ShadowConfig; outline: OutlineConfig; }

export function hexToRgb(hex: string): [number, number, number] {
    const h = hex.replace('#', '');
    const full = h.length === 3 ? h.split('').map(c => c + c).join('') : h.padEnd(6, '0').slice(0, 6);
    const num = parseInt(full, 16) || 0;
    return [(num >> 16) & 255, (num >> 8) & 255, num & 255];
}
export function hexToRgba(hex: string, alpha: number): string {
    const [r, g, b] = hexToRgb(hex);
    return `rgba(${r},${g},${b},${Math.max(0, Math.min(1, alpha))})`;
}
export function mixHex(a: string, b: string, t: number): string {
    const [r1, g1, b1] = hexToRgb(a), [r2, g2, b2] = hexToRgb(b);
    const mix = (x: number, y: number) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
    return `#${mix(r1, r2)}${mix(g1, g2)}${mix(b1, b2)}`;
}
export function relativeLuminance([r, g, b]: [number, number, number]): number {
    const srgb = [r, g, b].map(v => {
        const c = v / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });
    return 0.2126 * srgb[0] + 0.7152 * srgb[1] + 0.0722 * srgb[2];
}
export function contrastRatio(hex1: string, hex2: string): number {
    const l1 = relativeLuminance(hexToRgb(hex1));
    const l2 = relativeLuminance(hexToRgb(hex2));
    const [lighter, darker] = l1 > l2 ? [l1, l2] : [l2, l1];
    return (lighter + 0.05) / (darker + 0.05);
}
export function isLightColor(hex: string): boolean {
    return relativeLuminance(hexToRgb(hex)) > 0.5;
}
/** Assumed neutral surface color behind text when the real background is an unknown
 * screenshot — mixed toward black by the scrim darkness already in effect. This is a
 * deterministic estimate from known editor state, never pixel/image analysis. */
export function scrimSurfaceColor(darkness: number): string {
    const v = Math.round(140 * (1 - Math.max(0, Math.min(1, darkness))));
    const hex = v.toString(16).padStart(2, '0');
    return `#${hex}${hex}${hex}`;
}

/**
 * Auto text-effect: a cheap, deterministic heuristic over already-known editor state
 * (text color, the surface it sits on, and how much scrim/darkness is already helping)
 * — never pixel-level image analysis. Heavier scrim backs the added effect off since
 * it's already doing the contrast work; low computed contrast adds a thin outline too.
 */
export function computeAutoTextEffect(textColor: string, surfaceColor: string, scrimDarkness = 0): TextEffectConfig {
    const textIsLight = isLightColor(textColor);
    const anchor = textIsLight ? '#000000' : '#ffffff';
    const contrast = contrastRatio(textColor, surfaceColor);
    const scrimFactor = Math.max(0.25, 1 - scrimDarkness * 0.75);

    if (contrast >= 7) {
        return {
            mode: 'auto',
            shadow: { color: anchor, opacity: +(0.35 * scrimFactor).toFixed(2), blur: 10, offsetX: 0, offsetY: 2 },
            outline: { color: anchor, width: 0 },
        };
    }
    if (contrast >= 3.5) {
        return {
            mode: 'auto',
            shadow: { color: anchor, opacity: +(0.55 * scrimFactor).toFixed(2), blur: 14, offsetX: 0, offsetY: 3 },
            outline: { color: anchor, width: 0 },
        };
    }
    return {
        mode: 'auto',
        shadow: { color: anchor, opacity: +(0.7 * scrimFactor).toFixed(2), blur: 18, offsetX: 0, offsetY: 3 },
        outline: { color: anchor, width: 2 },
    };
}

function resolveTextEffect(
    mode: TextEffectMode,
    customShadow: ShadowConfig | undefined,
    customOutline: OutlineConfig | undefined,
    textColor: string,
    surfaceColor: string,
    scrimDarkness: number,
): TextEffectConfig {
    if (mode === 'none') {
        return { mode: 'none', shadow: { color: '#000000', opacity: 0, blur: 0, offsetX: 0, offsetY: 0 }, outline: { color: '#000000', width: 0 } };
    }
    if (mode === 'custom') {
        return {
            mode: 'custom',
            shadow: customShadow ?? { color: '#000000', opacity: 0.7, blur: 16, offsetX: 0, offsetY: 3 },
            outline: customOutline ?? { color: '#000000', width: 0 },
        };
    }
    return computeAutoTextEffect(textColor, surfaceColor, scrimDarkness);
}

/** Shared by both layout modes — unifies what used to be two independently hardcoded shadow blocks. */
function drawTitleText(ctx: CanvasRenderingContext2D, text: string, x: number, y: number, effect: TextEffectConfig) {
    if (effect.mode === 'none' || effect.shadow.opacity <= 0) {
        ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    } else {
        ctx.shadowColor = hexToRgba(effect.shadow.color, effect.shadow.opacity);
        ctx.shadowBlur = effect.shadow.blur;
        ctx.shadowOffsetX = effect.shadow.offsetX;
        ctx.shadowOffsetY = effect.shadow.offsetY;
    }
    ctx.fillText(text, x, y);
    ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
    if (effect.outline.width > 0) {
        ctx.lineWidth = effect.outline.width;
        ctx.strokeStyle = effect.outline.color;
        ctx.strokeText(text, x, y);
    }
}

/**
 * Wraps text to maxWidth, targeting maxLines by progressively shrinking the font down
 * to minFontSize; if it's still over maxLines at the minimum size, allows one extra
 * line and ellipsis-clips it rather than shrinking further into illegibility.
 */
export function wrapAndFitTitle(
    ctx: CanvasRenderingContext2D,
    text: string,
    maxWidth: number,
    fontFamily: string,
    startFontSize: number,
    minFontSize: number,
    maxLines: number,
): { lines: string[]; fontSize: number } {
    const wrapAt = (fs: number): string[] => {
        ctx.font = `800 ${fs}px ${fontFamily}`;
        const words = text.split(/\s+/).filter(Boolean);
        const lines: string[] = [];
        let cur = '';
        for (const w of words) {
            const t = cur ? `${cur} ${w}` : w;
            if (ctx.measureText(t).width > maxWidth && cur) { lines.push(cur); cur = w; } else cur = t;
        }
        if (cur) lines.push(cur);
        return lines.length ? lines : [''];
    };

    let fs = startFontSize;
    let lines = wrapAt(fs);
    while (lines.length > maxLines && fs > minFontSize) {
        fs = Math.max(minFontSize, fs - 2);
        lines = wrapAt(fs);
    }
    if (lines.length > maxLines) {
        const kept = lines.slice(0, maxLines);
        let last = kept[maxLines - 1];
        ctx.font = `800 ${fs}px ${fontFamily}`;
        const rest = lines.slice(maxLines - 1).join(' ');
        let clipped = rest;
        while (ctx.measureText(clipped + '…').width > maxWidth && clipped.length > 1) {
            clipped = clipped.slice(0, -1);
        }
        kept[maxLines - 1] = clipped.trimEnd() + '…';
        lines = kept;
    }
    return { lines, fontSize: fs };
}

// ── Image fit + focal-point drawing ─────────────────────────────────────────────
const FOCAL_FRACTIONS: Record<FocalPosition, [number, number]> = {
    'top-left': [0, 0], 'top-center': [0.5, 0], 'top-right': [1, 0],
    'center-left': [0, 0.5], 'center': [0.5, 0.5], 'center-right': [1, 0.5],
    'bottom-left': [0, 1], 'bottom-center': [0.5, 1], 'bottom-right': [1, 1],
};
export const FOCAL_GRID: FocalPosition[] = [
    'top-left', 'top-center', 'top-right',
    'center-left', 'center', 'center-right',
    'bottom-left', 'bottom-center', 'bottom-right',
];
const CORNER_RADIUS_PX: Record<CornerRadiusSize, number> = { none: 0, small: 8, medium: 16, large: 28 };

function roundedRectPath(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
    const rr = Math.max(0, Math.min(r, w / 2, h / 2));
    ctx.beginPath();
    ctx.moveTo(x + rr, y);
    ctx.arcTo(x + w, y, x + w, y + h, rr);
    ctx.arcTo(x + w, y + h, x, y + h, rr);
    ctx.arcTo(x, y + h, x, y, rr);
    ctx.arcTo(x, y, x + w, y, rr);
    ctx.closePath();
}

/** "fill" = cover-crop honoring the focal point; "fit" = contain, centered by default,
 * biased toward the focal point across whichever axis has letterbox slack. `zoom` (fill only,
 * 0-1+) crops in tighter around the focal point instead of just covering the box — the
 * "Highlight Zoom" template uses this to punch into a specific formula/highlight rather than
 * showing the whole screenshot. 0 = unchanged (plain cover-crop). */
function drawImageFitted(
    ctx: CanvasRenderingContext2D, el: HTMLImageElement,
    dx: number, dy: number, dw: number, dh: number,
    fit: ImageFit, focal: FocalPosition, zoom: number = 0,
    focalXY?: [number, number], // continuous drag-picked point (0-1) — overrides the 9-point grid when set
) {
    const iw = el.naturalWidth || 1, ih = el.naturalHeight || 1;
    const [fx, fy] = focalXY ?? FOCAL_FRACTIONS[focal] ?? FOCAL_FRACTIONS.center;
    const scale = fit === 'fill' ? Math.max(dw / iw, dh / ih) * (1 + Math.max(0, zoom)) : Math.min(dw / iw, dh / ih);
    const drawW = iw * scale, drawH = ih * scale;
    if (fit === 'fill') {
        ctx.save();
        ctx.beginPath(); ctx.rect(dx, dy, dw, dh); ctx.clip();
        ctx.drawImage(el, dx - (drawW - dw) * fx, dy - (drawH - dh) * fy, drawW, drawH);
        ctx.restore();
    } else {
        ctx.drawImage(el, dx + (dw - drawW) * fx, dy + (dh - drawH) * fy, drawW, drawH);
    }
}

// ── Canvas helper: small "identity" badge (course/book name) ────────────────────
// A rounded pill instead of a plain line of text — gives every thumbnail in the channel the same
// recognizable small mark, so a viewer scanning a feed of videos can tell they're from the same
// series at a glance, without it competing for attention with the title itself.
function drawBadge(
    ctx: CanvasRenderingContext2D, text: string,
    x: number, y: number, align: 'left' | 'right',
    bgColor: string, textColor: string, fontFamily: string,
) {
    if (!text) return;
    const fontSize = 22;
    ctx.font = `700 ${fontSize}px ${fontFamily}`;
    const padX = 16, padY = 9;
    const textW = ctx.measureText(text).width;
    const boxW = textW + padX * 2, boxH = fontSize + padY * 2;
    const boxX = align === 'left' ? x : x - boxW;
    ctx.save();
    roundedRectPath(ctx, boxX, y, boxW, boxH, boxH / 2);
    ctx.fillStyle = bgColor;
    ctx.fill();
    ctx.restore();
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = textColor;
    ctx.fillText(text, boxX + padX, y + boxH / 2 + 1);
    ctx.textBaseline = 'alphabetic';
}

// ── Canvas helper: split-mode single-screenshot cell ────────────────────────────
function drawScreenshotCell(
    ctx: CanvasRenderingContext2D, el: HTMLImageElement,
    rx: number, ry: number, rw: number, rh: number,
    fit: ImageFit, focal: FocalPosition, cornerR: number,
    margin = 14, focalXY?: [number, number], zoom: number = 0,
) {
    ctx.save();
    if (cornerR > 0) { roundedRectPath(ctx, rx, ry, rw, rh, cornerR); ctx.clip(); }
    else { ctx.beginPath(); ctx.rect(rx, ry, rw, rh); ctx.clip(); }

    if (fit === 'fill') {
        drawImageFitted(ctx, el, rx, ry, rw, rh, 'fill', focal, zoom, focalXY);
    } else {
        const iW = el.naturalWidth || 1, iH = el.naturalHeight || 1;
        const coverScale = Math.max(rw / iW, rh / iH);
        ctx.filter = 'blur(12px)'; ctx.globalAlpha = 0.88;
        ctx.drawImage(el, rx + (rw - iW * coverScale) / 2, ry + (rh - iH * coverScale) / 2, iW * coverScale, iH * coverScale);
        ctx.filter = 'none'; ctx.globalAlpha = 1;
        ctx.fillStyle = 'rgba(0,0,0,0.22)'; ctx.fillRect(rx, ry, rw, rh);
        ctx.shadowColor = 'rgba(0,0,0,0.4)'; ctx.shadowBlur = 12;
        drawImageFitted(ctx, el, rx + margin, ry + margin, rw - margin * 2, rh - margin * 2, 'fit', focal, 0, focalXY);
        ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0;
    }
    ctx.restore();
}

// ── Thumbnail generator ───────────────────────────────────────────────────────
const DEFAULT_ACCENT = '#f97316';
const DEFAULT_FONT_FAMILY = 'Arial, sans-serif';

export interface GenThumbParams {
    title: string;
    notebookTitle: string;
    presetId?: ThumbPresetId;
    screenshotEls?: HTMLImageElement[];
    coverEl?: HTMLImageElement | null;
    customColor?: string | null;
    style?: ThumbStyle;
    tagline?: string;
    subtitleColor?: string;
    subtitleScale?: number;
    bgBlur?: number;
    bgDark?: number;
    textStripDark?: number;
    titleStyle?: {
        color?: string;
        fontFamily?: string;
        scale?: number;
        alignment?: TitleAlign;
        placement?: TitlePlacement;
        textEffect?: { mode?: TextEffectMode; shadow?: ShadowConfig; outline?: OutlineConfig };
    };
    accentStyle?: { color?: string; width?: number };
    splitRatio?: number; // split style only — fraction of width given to the left screenshot panel (max 0.5)
    leftBgColor?: string | null; // split style only — solid fill behind a smaller screenshot stack instead of the default full-bleed image treatment
    bgSolidColor?: string | null; // screenshot style only — solid fill instead of the default blurred-screenshot backdrop
    imageStyle?: { fit?: ImageFit; focalPosition?: FocalPosition; cornerRadius?: CornerRadiusSize; zoom?: number; focalX?: number; focalY?: number; fakeStack?: boolean };
    watermark?: { enabled?: boolean; opacity?: number };
    penStrokes?: PenStroke[]; // drawn last, on top of everything — canvas-space, see PenStroke's doc comment
}

/** Returns a full data URL (data:image/png;base64,...) */
export function generateThumbnail({
    title,
    notebookTitle,
    presetId = 'amber',
    screenshotEls = [],
    coverEl = null,
    customColor = null,
    style = 'split',
    tagline = '',
    subtitleColor = '#fcd34d',
    subtitleScale = 1,
    bgBlur = 10,
    bgDark = 0.42,
    textStripDark = 0.75,
    titleStyle = {},
    accentStyle = {},
    splitRatio = 0.5,
    leftBgColor = null,
    bgSolidColor = null,
    imageStyle = {},
    watermark = {},
    penStrokes = [],
}: GenThumbParams): string {
    const preset = THUMB_PRESETS.find(p => p.id === presetId) || THUMB_PRESETS[0];
    const bgFrom = customColor ?? preset.from;
    const bgTo = customColor ?? preset.to;
    const W = 1280, H = 720;
    const canvas = document.createElement('canvas');
    canvas.width = W; canvas.height = H;
    const ctx = canvas.getContext('2d')!;

    const ts = {
        color: titleStyle.color ?? '#ffffff',
        fontFamily: titleStyle.fontFamily ?? DEFAULT_FONT_FAMILY,
        scale: titleStyle.scale ?? 1,
        alignment: titleStyle.alignment ?? (isOverlayStyle(style) ? 'center' : 'left') as TitleAlign,
        placement: titleStyle.placement ?? (isOverlayStyle(style) ? 'bottom' : 'center') as TitlePlacement,
        textEffect: {
            mode: titleStyle.textEffect?.mode ?? 'auto' as TextEffectMode,
            shadow: titleStyle.textEffect?.shadow,
            outline: titleStyle.textEffect?.outline,
        },
    };
    const acc = { color: accentStyle.color ?? DEFAULT_ACCENT, width: accentStyle.width ?? 4 };
    const img = {
        fit: imageStyle.fit ?? 'fit' as ImageFit,
        focalPosition: imageStyle.focalPosition ?? 'center' as FocalPosition,
        cornerRadius: imageStyle.cornerRadius ?? 'none' as CornerRadiusSize,
        zoom: imageStyle.zoom ?? 0,
        focalXY: (imageStyle.focalX != null && imageStyle.focalY != null) ? [imageStyle.focalX, imageStyle.focalY] as [number, number] : undefined,
    };
    const cornerRPx = CORNER_RADIUS_PX[img.cornerRadius];
    const wm = { enabled: watermark.enabled ?? true, opacity: watermark.opacity };

    const hasScreenshots = screenshotEls.length > 0;
    // Fakes a multi-screenshot collage even with just 1 image selected — same rotated/offset stack
    // treatment a real multi-image selection gets, purely for visual depth when explicitly asked for.
    const effEls = (imageStyle.fakeStack && screenshotEls.length === 1) ? [screenshotEls[0], screenshotEls[0], screenshotEls[0]] : screenshotEls;

    // Fixed scatter params for up to 4 images — template-style, not user-facing.
    const ROTS = [-9, 7, -5, 11];
    const OFX  = [-20, 24, -10, 18];
    const OFY  = [-14, 20, 24, -20];

    // topZoom/topFocalXY apply only to the LAST element (drawn on top, i.e. the frontmost card) —
    // so the zoom/pan controls keep working on "the top image" even in the scattered/stack layout,
    // instead of going dead the moment 2+ images (or a faked stack of 1) trigger this path. The
    // background cards behind it stay plain "fit" thumbnails, which is what actually reads as depth.
    const drawScattered = (els: HTMLImageElement[], cx: number, cy: number, imgW: number, imgH: number, topZoom: number = 0, topFocalXY?: [number, number], topFit: ImageFit = 'fit') => {
        els.forEach((el, i) => {
            const isTop = i === els.length - 1;
            const rot = (ROTS[i] ?? 0) * Math.PI / 180;
            const tx = cx + (OFX[i] ?? 0), ty = cy + (OFY[i] ?? 0);
            const elW = el.naturalWidth || 1, elH = el.naturalHeight || 1;
            ctx.save();
            ctx.translate(tx, ty); ctx.rotate(rot);
            ctx.shadowColor = 'rgba(0,0,0,0.72)'; ctx.shadowBlur = 32;
            ctx.shadowOffsetX = 6; ctx.shadowOffsetY = 14;
            ctx.fillStyle = 'rgba(0,0,0,0.01)'; ctx.fillRect(-imgW / 2, -imgH / 2, imgW, imgH);
            ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0; ctx.shadowOffsetX = 0; ctx.shadowOffsetY = 0;
            ctx.save();
            ctx.beginPath(); ctx.rect(-imgW / 2, -imgH / 2, imgW, imgH); ctx.clip();
            // The top (frontmost) card honors the same Fit/Fill choice as everywhere else — it used to
            // only switch to a fill-crop when zoom/focal had actually been touched, so toggling
            // "Lấp đầy" alone silently did nothing while a stack was showing.
            if (isTop && topFit === 'fill') {
                const [fx, fy] = topFocalXY ?? [0.5, 0.5];
                const scFill = Math.max(imgW / elW, imgH / elH) * (1 + Math.max(0, topZoom));
                const dw = elW * scFill, dh = elH * scFill;
                ctx.drawImage(el, -imgW / 2 - (dw - imgW) * fx, -imgH / 2 - (dh - imgH) * fy, dw, dh);
            } else {
                const sc = Math.min(imgW / elW, imgH / elH);
                const dw = elW * sc, dh = elH * sc;
                ctx.drawImage(el, -dw / 2, -dh / 2, dw, dh);
            }
            ctx.restore(); ctx.restore();
        });
    };

    const drawBookCover = (bx: number, by: number, bookW: number, bookH: number, shadowBlur: number, offX: number, offY: number, spineW: number) => {
        if (!coverEl) return;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.7)'; ctx.shadowBlur = shadowBlur;
        ctx.shadowOffsetX = offX; ctx.shadowOffsetY = offY;
        ctx.beginPath(); (ctx as any).roundRect(bx, by, bookW, bookH, [3, 3, 0, 0]);
        ctx.fillStyle = '#000'; ctx.fill(); ctx.restore();
        ctx.save(); ctx.beginPath();
        (ctx as any).roundRect(bx, by, bookW, bookH, [3, 3, 0, 0]); ctx.clip();
        drawImageFitted(ctx, coverEl, bx, by, bookW, bookH, 'fill', img.focalPosition, 0, img.focalXY);
        ctx.restore();
        const spGrad = ctx.createLinearGradient(bx, 0, bx + spineW, 0);
        spGrad.addColorStop(0, 'rgba(255,255,255,0.28)'); spGrad.addColorStop(1, 'rgba(255,255,255,0)');
        ctx.fillStyle = spGrad; ctx.fillRect(bx, by, spineW, bookH);
    };

    if (style === 'title') {
        // Backdrop: a chosen solid colour, else the book cover blurred beyond recognition (it only
        // lends its colours), else the preset gradient.
        if (bgSolidColor) {
            ctx.fillStyle = bgSolidColor; ctx.fillRect(0, 0, W, H);
        } else if (coverEl) {
            const cW = coverEl.naturalWidth || 1, cH = coverEl.naturalHeight || 1;
            const cs = Math.max(W / cW, H / cH) * 1.25; // overscan so the blur has no transparent edge
            ctx.filter = `blur(${Math.max(18, bgBlur * 3)}px)`;
            ctx.drawImage(coverEl, (W - cW * cs) / 2, (H - cH * cs) / 2, cW * cs, cH * cs);
            ctx.filter = 'none';
            ctx.fillStyle = `rgba(0,0,0,${bgDark})`; ctx.fillRect(0, 0, W, H);
        } else {
            const grad = ctx.createLinearGradient(0, 0, W, H);
            grad.addColorStop(0, bgFrom); grad.addColorStop(1, bgTo);
            ctx.fillStyle = grad; ctx.fillRect(0, 0, W, H);
            ctx.fillStyle = `rgba(0,0,0,${bgDark * 0.5})`; ctx.fillRect(0, 0, W, H);
        }

        // The label: a dark rounded panel holding the title, with the accent as a bar on its left edge.
        const side = 70, innerPad = 48;
        const panelW = W - side * 2, maxW = panelW - innerPad * 2 - acc.width;
        const { lines, fontSize: fs } = wrapAndFitTitle(ctx, title || 'StudyBoard', maxW, ts.fontFamily, Math.round(92 * ts.scale), 40, 3);
        const lineH = fs * 1.2;
        const tagFs = Math.round(fs * 0.42);
        const tagH = tagline ? tagFs + 22 : 0;
        const panelH = lines.length * lineH + tagH + innerPad * 2 - fs * 0.12;
        const panelY = ts.placement === 'top' ? 118 : ts.placement === 'center' ? (H - panelH) / 2 + 12 : H - panelH - 74;
        ctx.save();
        ctx.shadowColor = 'rgba(0,0,0,0.45)'; ctx.shadowBlur = 36; ctx.shadowOffsetY = 10;
        roundedRectPath(ctx, side, panelY, panelW, panelH, 28);
        ctx.fillStyle = `rgba(0,0,0,${Math.min(0.35 + textStripDark * 0.5, 0.9).toFixed(2)})`;
        ctx.fill();
        ctx.restore();
        if (acc.color !== 'transparent') {
            ctx.save();
            roundedRectPath(ctx, side, panelY, panelW, panelH, 28); ctx.clip();
            ctx.fillStyle = acc.color; ctx.fillRect(side, panelY, Math.max(acc.width * 2.5, 10), panelH);
            ctx.restore();
        }
        const textLeft = side + innerPad + acc.width, textRight = side + panelW - innerPad;
        const anchorX = ts.alignment === 'left' ? textLeft : ts.alignment === 'right' ? textRight : (textLeft + textRight) / 2;
        ctx.textAlign = ts.alignment;
        ctx.font = `800 ${fs}px ${ts.fontFamily}`;
        ctx.fillStyle = ts.color;
        const surface = scrimSurfaceColor(textStripDark);
        const effect = resolveTextEffect(ts.textEffect.mode, ts.textEffect.shadow, ts.textEffect.outline, ts.color, surface, textStripDark);
        const firstBase = panelY + innerPad + fs * 0.82;
        lines.forEach((l, i) => drawTitleText(ctx, l, anchorX, firstBase + i * lineH, effect));
        if (tagline) {
            ctx.font = `600 ${tagFs}px ${ts.fontFamily}`;
            ctx.fillStyle = hexToRgba(ts.color, 0.8);
            ctx.fillText(tagline, anchorX, firstBase + (lines.length - 1) * lineH + tagH + 4);
        }

        if (notebookTitle) {
            const badgeText = notebookTitle.length > 28 ? notebookTitle.slice(0, 25) + '…' : notebookTitle;
            drawBadge(ctx, badgeText, 32, 40, 'left', subtitleColor, isLightColor(subtitleColor) ? '#111111' : '#ffffff', ts.fontFamily);
        }
        // Book cover, kept sharp in the corner the label does not occupy.
        if (coverEl) {
            const bookW = 96;
            const ratio = coverEl.naturalHeight / Math.max(coverEl.naturalWidth, 1);
            const bookH = Math.min(Math.round(bookW * ratio), 146);
            const bookY = ts.placement === 'top' ? H - bookH - 44 : 30;
            drawBookCover(W - bookW - 36, bookY, bookW, bookH, 18, -3, 6, 10);
        }
        if (wm.enabled) {
            ctx.textAlign = 'center';
            ctx.fillStyle = hexToRgba('#ffffff', wm.opacity ?? 0.6);
            ctx.font = '500 22px Arial, sans-serif';
            ctx.fillText('studyboard.app', W / 2, H - 24);
        }

    } else if (style === 'screenshot' && hasScreenshots) {
        const primary = screenshotEls[0];
        const iW = primary.naturalWidth || 1, iH = primary.naturalHeight || 1;
        if (bgSolidColor) {
            // Solid color instead of the blurred-screenshot backdrop — no darkening on top, since a
            // deliberately picked color is already under the user's own contrast control.
            ctx.fillStyle = bgSolidColor;
            ctx.fillRect(0, 0, W, H);
        } else {
            // Full-width blurred background — decorative backdrop, always cover/centered.
            const s = Math.max(W / iW, H / iH);
            ctx.filter = `blur(${bgBlur}px)`; ctx.globalAlpha = 0.9;
            ctx.drawImage(primary, (W - iW * s) / 2, (H - iH * s) / 2, iW * s, iH * s);
            ctx.filter = 'none'; ctx.globalAlpha = 1;
            ctx.fillStyle = `rgba(0,0,0,${bgDark})`; ctx.fillRect(0, 0, W, H);
        }

        if (effEls.length === 1) {
            // boxY leaves clearance under the course-name badge (drawn at y=32, ~40px tall) — it used
            // to start at 28, almost exactly where the badge sits, so the image visually collided
            // with it right at the top edge.
            // Badge sits at y=40, height ~40 -> bottom edge at y=80. boxY needs real clearance below
            // that (previous "fix" moved both numbers up in tandem and left only a ~4px gap — visually
            // indistinguishable from touching, especially with the box's own 24px shadow blur bleeding
            // upward into it).
            const boxX = 40, boxY = 112, boxW = W - 80, boxH = H * 0.52;
            ctx.save();
            if (cornerRPx > 0) { roundedRectPath(ctx, boxX, boxY, boxW, boxH, cornerRPx); ctx.clip(); }
            ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 24;
            if (img.fit === 'fill') {
                drawImageFitted(ctx, primary, boxX, boxY, boxW, boxH, 'fill', img.focalPosition, img.zoom, img.focalXY);
            } else {
                const cs = Math.min(boxW / iW, boxH / iH);
                ctx.drawImage(primary, (W - iW * cs) / 2, boxY, iW * cs, iH * cs);
            }
            ctx.shadowColor = 'transparent'; ctx.shadowBlur = 0;
            ctx.restore();
        } else {
            // Hard safety clip around the whole stack — the cards are rotated (up to 11°) and offset
            // (ROTS/OFX/OFY), which inflates their effective bounding box well past imgW x imgH in a
            // way that's easy to get wrong by hand-tuning cy/size (as the previous pass did — still
            // reached past the badge in some rotation/offset combos). Clipping the OUTER draw region
            // to a fixed safe rectangle guarantees no card can visually spill past it, regardless of
            // the exact per-card rotation/offset numbers.
            const stackSafeTop = 108, stackSafeBottom = H - 170, stackSafeX = 56;
            ctx.save();
            ctx.beginPath(); ctx.rect(stackSafeX, stackSafeTop, W - stackSafeX * 2, stackSafeBottom - stackSafeTop); ctx.clip();
            // Reversed so the FIRST selected image is drawn last (frontmost card) — matching the
            // focal-point drag preview, which always shows orderedIdxs[0].
            drawScattered([...effEls.slice(0, 4)].reverse(), W / 2, (stackSafeTop + stackSafeBottom) / 2,
                Math.round(W * 0.55), Math.round(H * 0.5), img.zoom, img.focalXY, img.fit);
            ctx.restore();
        }

        // Text overlay
        const pad = 70, maxW = W - pad * 2;
        const { lines, fontSize: fs } = wrapAndFitTitle(ctx, title || 'StudyBoard', maxW, ts.fontFamily, Math.round(72 * ts.scale), 34, 2);
        const lineH = fs * 1.18;
        const tagH = tagline ? fs * 0.45 + 12 : 0;
        const textBlockH = lines.length * lineH + tagH + 36;
        const blockY = ts.placement === 'top' ? 40 : ts.placement === 'center' ? (H - textBlockH) / 2 : H - textBlockH - 30;
        const pad2 = 40;
        const stripY = blockY - pad2, stripH = textBlockH + pad2 * 2;
        const s0 = Math.min(textStripDark * 0.73, 1).toFixed(2);
        const s1 = Math.min(textStripDark, 1).toFixed(2);
        const strip = ctx.createLinearGradient(0, stripY, 0, stripY + stripH);
        if (ts.placement === 'center') {
            strip.addColorStop(0, 'rgba(0,0,0,0)');
            strip.addColorStop(0.2, `rgba(0,0,0,${s0})`);
            strip.addColorStop(0.5, `rgba(0,0,0,${s1})`);
            strip.addColorStop(0.8, `rgba(0,0,0,${s0})`);
            strip.addColorStop(1, 'rgba(0,0,0,0)');
        } else {
            strip.addColorStop(0, 'rgba(0,0,0,0)');
            strip.addColorStop(0.4, `rgba(0,0,0,${s0})`);
            strip.addColorStop(1, `rgba(0,0,0,${s1})`);
        }
        ctx.fillStyle = strip; ctx.fillRect(0, stripY, W, stripH);

        const anchorX = ts.alignment === 'left' ? pad : ts.alignment === 'right' ? W - pad : W / 2;
        ctx.textAlign = ts.alignment;
        ctx.font = `800 ${fs}px ${ts.fontFamily}`;
        ctx.fillStyle = ts.color;
        const surface = scrimSurfaceColor(textStripDark);
        const effect = resolveTextEffect(ts.textEffect.mode, ts.textEffect.shadow, ts.textEffect.outline, ts.color, surface, textStripDark);
        lines.forEach((l, i) => drawTitleText(ctx, l, anchorX, blockY + (i + 1) * lineH, effect));
        if (tagline) {
            ctx.font = `600 ${Math.round(fs * 0.42)}px ${ts.fontFamily}`;
            ctx.fillStyle = hexToRgba(ts.color, 0.8);
            ctx.fillText(tagline, anchorX, blockY + lines.length * lineH + tagH);
        }
        if (wm.enabled) {
            ctx.textAlign = 'center';
            ctx.fillStyle = hexToRgba(ts.color, wm.opacity ?? 0.73);
            ctx.font = '500 22px Arial, sans-serif';
            ctx.fillText('studyboard.app', W / 2, H - 22);
        }

        if (notebookTitle) {
            const badgeText = notebookTitle.length > 28 ? notebookTitle.slice(0, 25) + '…' : notebookTitle;
            drawBadge(ctx, badgeText, 32, 40, 'left', subtitleColor, isLightColor(subtitleColor) ? '#111111' : '#ffffff', ts.fontFamily);
        }

        // Book cover — bottom-right, same as split style
        if (coverEl) {
            const bookW = 90;
            const ratio = coverEl.naturalHeight / Math.max(coverEl.naturalWidth, 1);
            const bookH = Math.min(Math.round(bookW * ratio), 140);
            drawBookCover(W - bookW - 32, H - bookH - 28, bookW, bookH, 18, -3, 6, 10);
        }

    } else {
        // Split: scattered screenshots on left, gradient text panel on right
        const ratio = Math.min(0.5, Math.max(0.15, splitRatio));
        const splitX = hasScreenshots ? Math.round(W * ratio) : W;

        if (hasScreenshots) {
            const panelW = splitX;
            // Reversed so the FIRST selected image ends up drawn last (visually on top, frontmost
            // card) — matching the focal-point drag preview, which always shows orderedIdxs[0]. Without
            // this, dragging the pan handle visibly moved a different image than the one on top.
            const els = [...effEls.slice(0, 4)].reverse();
            // Same reasoning as the screenshot-style stack: rotated/offset cards can spill past their
            // nominal box, so the whole scattered draw is clipped to the panel bounds — never past the
            // divider into the text side, never past the canvas top/bottom.
            if (leftBgColor) {
                // Solid-color panel with a smaller screenshot stack on top, rather than the
                // screenshots filling the panel edge-to-edge — leaves the background color visible.
                ctx.fillStyle = leftBgColor; ctx.fillRect(0, 0, panelW, H);
                ctx.save();
                ctx.beginPath(); ctx.rect(0, 0, panelW, H); ctx.clip();
                drawScattered(els, panelW / 2, H / 2,
                    Math.round(panelW * 0.62), Math.round(H * 0.56), img.zoom, img.focalXY, img.fit);
                ctx.restore();
            } else if (els.length === 1) {
                drawScreenshotCell(ctx, els[0], 0, 0, panelW, H, img.fit, img.focalPosition, cornerRPx, 14, img.focalXY, img.zoom);
            } else {
                ctx.save();
                ctx.beginPath(); ctx.rect(0, 0, panelW, H); ctx.clip();
                const bg = els[0];
                const bgIW = bg.naturalWidth || 1, bgIH = bg.naturalHeight || 1;
                const bgS = Math.max(panelW / bgIW, H / bgIH);
                ctx.filter = 'blur(14px)'; ctx.globalAlpha = 0.7;
                ctx.drawImage(bg, (panelW - bgIW * bgS) / 2, (H - bgIH * bgS) / 2, bgIW * bgS, bgIH * bgS);
                ctx.filter = 'none'; ctx.globalAlpha = 1;
                ctx.fillStyle = 'rgba(0,0,0,0.35)'; ctx.fillRect(0, 0, panelW, H);
                drawScattered(els, panelW / 2, H / 2,
                    Math.round(panelW * 0.70), Math.round(H * 0.65), img.zoom, img.focalXY, img.fit);
                ctx.restore();
            }
            if (!leftBgColor) {
                const edgeGrad = ctx.createLinearGradient(splitX - 80, 0, splitX, 0);
                edgeGrad.addColorStop(0, 'rgba(0,0,0,0)'); edgeGrad.addColorStop(1, 'rgba(0,0,0,0.45)');
                ctx.fillStyle = edgeGrad; ctx.fillRect(splitX - 80, 0, 80, H);
            }
        }

        // Right gradient panel
        const grad = ctx.createLinearGradient(splitX, 0, W, H);
        grad.addColorStop(0, bgFrom); grad.addColorStop(1, bgTo);
        ctx.fillStyle = grad; ctx.fillRect(splitX, 0, W - splitX, H);
        if (hasScreenshots && acc.color !== 'transparent') { ctx.fillStyle = acc.color; ctx.fillRect(splitX, 0, acc.width, H); }

        // Decorative circles — template-style, tied to the preset's own accent circle color.
        ctx.save(); ctx.globalAlpha = 0.05; ctx.fillStyle = preset.circle;
        for (let i = 0; i < 5; i++) {
            ctx.beginPath();
            ctx.arc(W - 80 + i * 12, H - 80 + i * 10, 130 - i * 16, 0, Math.PI * 2);
            ctx.fill();
        }
        ctx.restore();

        // Book cover
        if (coverEl) {
            const bookW = 110;
            const ratio = coverEl.naturalHeight / Math.max(coverEl.naturalWidth, 1);
            const bookH = Math.min(Math.round(bookW * ratio), 170);
            drawBookCover(W - bookW - 36, H - bookH - 30, bookW, bookH, 20, -4, 8, 12);
        }

        // Text
        const padding = 60, textX = splitX + padding;
        const maxW = (W - splitX) - padding * 2;
        const { lines, fontSize: fs } = wrapAndFitTitle(ctx, title || 'StudyBoard', maxW, ts.fontFamily, Math.round((hasScreenshots ? 84 : 110) * ts.scale), 36, 2);
        const lineH = fs * 1.2, totalH = lines.length * lineH;
        const startY = ts.placement === 'top' ? 60
            : ts.placement === 'bottom' ? H - totalH - 140
            : H / 2 - totalH / 2 - 20;
        ctx.textAlign = 'left';
        ctx.font = `800 ${fs}px ${ts.fontFamily}`;
        ctx.fillStyle = ts.color;
        const surface = mixHex(bgFrom, bgTo, 0.5);
        const effect = resolveTextEffect(ts.textEffect.mode, ts.textEffect.shadow, ts.textEffect.outline, ts.color, surface, 0);
        lines.forEach((line, i) => drawTitleText(ctx, line, textX, startY + (i + 1) * lineH, effect));

        const subFs = Math.round((hasScreenshots ? 26 : 32) * subtitleScale);
        const subY = startY + (lines.length + 1) * lineH + 14;
        const sub = notebookTitle.length > 28 ? notebookTitle.slice(0, 25) + '…' : notebookTitle;
        const badgeTextColor = isLightColor(subtitleColor) ? '#111111' : '#ffffff';
        ctx.textAlign = 'left';
        if (tagline) {
            ctx.fillStyle = hexToRgba(ts.color, 0.8); ctx.font = `600 ${Math.round(subFs * 0.85)}px ${ts.fontFamily}`;
            ctx.fillText(tagline.length > 55 ? tagline.slice(0, 52) + '…' : tagline, textX, subY);
            if (notebookTitle) drawBadge(ctx, sub, textX, subY + subFs * 0.6, 'left', subtitleColor, badgeTextColor, ts.fontFamily);
        } else {
            if (notebookTitle) drawBadge(ctx, sub, textX, subY - subFs * 0.75, 'left', subtitleColor, badgeTextColor, ts.fontFamily);
        }
        if (wm.enabled) {
            ctx.fillStyle = hexToRgba('#ffffff', wm.opacity ?? 0.35); ctx.font = '500 22px Arial, sans-serif';
            ctx.fillText('studyboard.app', textX, H - 34);
        }
    }

    // Pen strokes draw last, on top of everything — a quick freehand mark (e.g. underline a formula)
    // stays visible regardless of what layout/background is underneath.
    for (const stroke of penStrokes) {
        if (stroke.points.length < 2) continue;
        ctx.save();
        ctx.strokeStyle = stroke.color;
        ctx.lineWidth = 5;
        ctx.lineCap = 'round'; ctx.lineJoin = 'round';
        ctx.shadowColor = 'rgba(0,0,0,0.5)'; ctx.shadowBlur = 4;
        ctx.beginPath();
        ctx.moveTo(stroke.points[0].x * W, stroke.points[0].y * H);
        for (const p of stroke.points.slice(1)) ctx.lineTo(p.x * W, p.y * H);
        ctx.stroke();
        ctx.restore();
    }

    return canvas.toDataURL('image/png');
}

// ── Small UI atoms ────────────────────────────────────────────────────────────
function parseColor(raw: string): string | null {
    const t = raw.trim();
    if (/^#[0-9a-f]{6}$/i.test(t) || /^#[0-9a-f]{3}$/i.test(t)) return t;
    if (/^[0-9a-f]{6}$/i.test(t)) return '#' + t;
    if (/^[0-9a-f]{3}$/i.test(t)) return '#' + t;
    const rgb = t.match(/^rgb\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*\)$/i);
    if (rgb) {
        const [r, g, b] = [+rgb[1], +rgb[2], +rgb[3]];
        if (r <= 255 && g <= 255 && b <= 255)
            return '#' + [r, g, b].map(n => n.toString(16).padStart(2, '0')).join('');
    }
    return null;
}

const ColorInput: React.FC<{ value: string; onChange: (c: string) => void }> = ({ value, onChange }) => {
    const [text, setText] = useState(value);
    useEffect(() => { setText(value); }, [value]);
    return (
        <div className="flex gap-2 items-center">
            <input type="color" value={value}
                onChange={e => { onChange(e.target.value); setText(e.target.value); }}
                className="w-10 h-9 rounded-lg border border-gray-200 dark:border-slate-600 cursor-pointer p-0.5 flex-shrink-0 bg-white dark:bg-slate-800" />
            <input type="text" value={text}
                onChange={e => { setText(e.target.value); const c = parseColor(e.target.value); if (c) onChange(c); }}
                placeholder="#ffffff"
                className="flex-1 text-sm font-mono border border-gray-200 dark:border-slate-600 rounded-lg px-2 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300 min-w-0" />
        </div>
    );
};

const ScaleSlider: React.FC<{ value: number; onChange: (n: number) => void; min?: number; max?: number }> = ({ value, onChange, min = 0.4, max = 2.0 }) => (
    <div className="flex items-center gap-2">
        <input type="range" min={min} max={max} step={0.05} value={value}
            onChange={e => onChange(Number(e.target.value))}
            className="flex-1 accent-red-500 h-1.5 cursor-pointer" />
        <span className="text-[11px] font-mono text-gray-500 w-8 text-right flex-shrink-0">{value.toFixed(2)}×</span>
    </div>
);

const MiniSlider: React.FC<{ value: number; onChange: (n: number) => void; min: number; max: number; step: number; fmt: (n: number) => string }> =
    ({ value, onChange, min, max, step, fmt }) => (
    <div className="flex items-center gap-2">
        <input type="range" min={min} max={max} step={step} value={value}
            onChange={e => onChange(Number(e.target.value))}
            className="flex-1 accent-red-500 h-1.5 cursor-pointer" />
        <span className="text-[11px] font-mono text-gray-500 w-10 text-right flex-shrink-0">{fmt(value)}</span>
    </div>
);

// ── Curated solid-color palettes — well-known designer color systems, not ad-hoc hex picks, for the
// "Nền"/"Màu nền trái" pickers below. Grouped into families (switch with the small tabs) rather than
// one giant swatch wall, since dumping 50+ colors in a row is worse than a curated 8-10 per family.
const COLOR_PALETTES: { id: string; labelVi: string; labelEn: string; colors: string[] }[] = [
    { id: 'flat', labelVi: 'Flat UI', labelEn: 'Flat UI', colors: ['#1abc9c', '#2ecc71', '#3498db', '#9b59b6', '#34495e', '#f1c40f', '#e67e22', '#e74c3c', '#2c3e50', '#c0392b'] },
    { id: 'material', labelVi: 'Material', labelEn: 'Material', colors: ['#f44336', '#e91e63', '#9c27b0', '#673ab7', '#3f51b5', '#2196f3', '#009688', '#4caf50', '#ff9800', '#795548'] },
    { id: 'tailwind', labelVi: 'Tailwind', labelEn: 'Tailwind', colors: ['#475569', '#ef4444', '#f97316', '#f59e0b', '#10b981', '#14b8a6', '#0ea5e9', '#6366f1', '#8b5cf6', '#f43f5e'] },
    { id: 'pastel', labelVi: 'Pastel', labelEn: 'Pastel', colors: ['#fecaca', '#fed7aa', '#fef08a', '#bbf7d0', '#a7f3d0', '#bfdbfe', '#c7d2fe', '#ddd6fe', '#fbcfe8', '#e5e7eb'] },
    { id: 'neutral', labelVi: 'Trung tính', labelEn: 'Neutral', colors: ['#000000', '#111827', '#1f2937', '#374151', '#6b7280', '#9ca3af', '#d1d5db', '#e5e7eb', '#f3f4f6', '#ffffff'] },
    { id: 'earth', labelVi: 'Đất', labelEn: 'Earth', colors: ['#451a03', '#78350f', '#92400e', '#a16207', '#854d0e', '#7c2d12', '#713f12', '#57534e'] },
    { id: 'nord', labelVi: 'Nord', labelEn: 'Nord', colors: ['#2e3440', '#3b4252', '#434c5e', '#4c566a', '#5e81ac', '#81a1c1', '#88c0d0', '#8fbcbb', '#a3be8c', '#b48ead', '#bf616a', '#d08770'] },
    { id: 'dracula', labelVi: 'Dracula', labelEn: 'Dracula', colors: ['#282a36', '#44475a', '#ff5555', '#ff79c6', '#bd93f9', '#8be9fd', '#50fa7b', '#f1fa8c', '#ffb86c'] },
    { id: 'gruvbox', labelVi: 'Gruvbox', labelEn: 'Gruvbox', colors: ['#282828', '#3c3836', '#fb4934', '#fe8019', '#fabd2f', '#b8bb26', '#8ec07c', '#83a598', '#d3869b', '#928374'] },
    { id: 'vaporwave', labelVi: 'Vaporwave', labelEn: 'Vaporwave', colors: ['#300350', '#153cb4', '#b967ff', '#ff71ce', '#f62e97', '#01cdfe', '#05ffa1', '#fffb96'] },
];
const ALL_PALETTE_COLORS = COLOR_PALETTES.flatMap(p => p.colors);

const ColorPaletteSwatchPicker: React.FC<{
    value: string | null;
    onChange: (c: string | null) => void;
    vn: boolean;
    noneTitle: string;
}> = ({ value, onChange, vn, noneTitle }) => {
    const [paletteId, setPaletteId] = useState(COLOR_PALETTES[0].id);
    const pickerRef = useRef<HTMLInputElement>(null);
    const palette = COLOR_PALETTES.find(p => p.id === paletteId) || COLOR_PALETTES[0];
    const isCustom = !!value && !ALL_PALETTE_COLORS.includes(value);
    return (
        <div>
            <div className="flex items-center gap-1 mb-1.5 flex-wrap">
                {COLOR_PALETTES.map(p => (
                    <button key={p.id} onClick={() => setPaletteId(p.id)}
                        className={`px-1.5 py-0.5 text-[9px] font-bold rounded-md border transition-colors ${paletteId === p.id ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                        {vn ? p.labelVi : p.labelEn}
                    </button>
                ))}
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
                <button onClick={() => onChange(null)} title={noneTitle}
                    className={`w-6 h-6 rounded-full border-2 flex-shrink-0 ${!value ? 'border-blue-400' : 'border-transparent hover:scale-110'} transition-transform`}
                    style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, #f3f4f6 0% 50%)', backgroundSize: '6px 6px' }} />
                {palette.colors.map(c => (
                    <button key={c} onClick={() => onChange(c)} title={c}
                        className={`w-6 h-6 rounded-full border-2 transition-transform ${value === c ? 'border-white scale-125 shadow-md ring-1 ring-gray-300 dark:ring-slate-500' : 'border-transparent hover:scale-110'}`}
                        style={{ background: c }} />
                ))}
                <button onClick={() => pickerRef.current?.click()} title={vn ? 'Màu tùy chỉnh' : 'Custom'}
                    className={`w-6 h-6 rounded-full border-2 overflow-hidden transition-transform hover:scale-110 ${isCustom ? 'border-white scale-125 shadow-md' : 'border-transparent'}`}
                    style={{ background: isCustom ? value! : 'conic-gradient(red,yellow,lime,aqua,blue,magenta,red)' }} />
                <input ref={pickerRef} type="color" className="hidden" value={value ?? '#0f172a'} onChange={e => onChange(e.target.value)} />
            </div>
        </div>
    );
};

// Everything the editor needs to reproduce a thumbnail exactly — stored on the video when its
// thumbnail is pushed to YouTube, so reopening the editor starts from what is actually live.
export interface ThumbSettings {
    v: 1;
    thumbStyle: ThumbStyle;
    thumbPreset: ThumbPresetId;
    customColor: string | null;
    title: string;
    tagline: string;
    titleColor: string;
    titleFontId: string;
    titleScale: number;
    titleAlign: TitleAlign;
    screenshotPlacement: TitlePlacement;
    splitPlacement: TitlePlacement;
    textEffectMode: TextEffectMode;
    customShadow: ShadowConfig;
    customShadowEnabled: boolean;
    customOutline: OutlineConfig;
    customOutlineEnabled: boolean;
    accentColor: string | null;
    accentWidth: number;
    splitRatio: number;
    leftBgColor: string | null;
    bgSolidColor: string | null;
    imageFit: ImageFit;
    focalPosition: FocalPosition;
    imageZoom?: number; // "Highlight Zoom" template only — crops in tighter around the focal point; absent/0 = unchanged
    focalX?: number; focalY?: number; // drag-picked focal point (0-1), overrides the 9-point focalPosition grid when set
    fakeStack?: boolean; // fakes a multi-screenshot collage even with just 1 image selected, for visual depth
    penStrokes?: PenStroke[]; // simple freehand pen marks, normalized 0-1 to the final 1280x720 canvas
    cornerRadius: CornerRadiusSize;
    watermarkEnabled: boolean;
    watermarkOpacity: number;
    subtitleText: string;
    subtitleColor: string;
    subtitleScale: number;
    bgBlur: number;
    bgDark: number;
    textStripDark: number;
    selectedIdxs: number[];
}

// ── Component ─────────────────────────────────────────────────────────────────
export interface ThumbnailEditorProps {
    title: string;
    notebookTitle: string;
    noteImages: { src: string; label: string }[];
    notebookId: string;
    language: Language;
    youtubeVideoId?: string;
    ytConnected?: boolean;
    onClose: () => void;
    /** Fired with full data URL on every thumbnail change */
    onThumbnailChange?: (dataUrl: string) => void;
    // Note content/image so the Title field can ask AI to read the note and suggest a title —
    // same generateTitleForNode call already used for the YouTube title field in Manager.
    noteContent?: string;
    noteImageBase64?: string | null;
    noteImageMimeType?: string | null;
    /** Settings of the thumbnail last pushed to this video — the editor starts from them. */
    initialSettings?: Partial<ThumbSettings> | null;
    settingsSavedAt?: number;
    /** Fired after a successful push from inside the editor, with the settings that produced it. */
    onPushed?: (settings: ThumbSettings) => void;
    /** Fired with the current settings whenever the thumbnail is regenerated. */
    onSettingsChange?: (settings: ThumbSettings | null) => void; // null = the preview is now a manually uploaded image
    /** Saved-but-not-pushed versions of this video's thumbnail (drafts UI only shows when onSaveDraft is given). */
    drafts?: ThumbDraft[];
    onSaveDraft?: (draft: ThumbDraft) => Promise<void> | void;
    onDeleteDraft?: (id: string) => Promise<void> | void;
    /** Image of what is currently live on YouTube, for the comparison view. */
    liveThumbUrl?: string;
    /** Tab switcher shown in the header when the editor shares a window with the Studio (same size as the Studio then). */
    tabs?: React.ReactNode;
}

const DEFAULT_STYLE = {
    titleColor: '#ffffff',
    titleFontId: THUMB_FONTS[0].id,
    titleScale: 1,
    titleAlign: 'center' as TitleAlign,
    screenshotPlacement: 'bottom' as TitlePlacement,
    splitPlacement: 'center' as TitlePlacement,
    textEffectMode: 'auto' as TextEffectMode,
    accentColor: null as string | null,
    accentWidth: 4,
    splitRatio: 0.5,
    leftBgColor: null as string | null,
    bgSolidColor: null as string | null,
    imageFit: 'fit' as ImageFit,
    focalPosition: 'center' as FocalPosition,
    cornerRadius: 'none' as CornerRadiusSize,
    watermarkEnabled: true,
    watermarkOpacity: 0.35,
    subtitleColor: '#fcd34d',
    subtitleScale: 1,
    bgBlur: 10,
    bgDark: 0.42,
    textStripDark: 0.75,
};

// ── Settings <-> image helpers (shared by the editor, drafts and bulk style paste) ──────────────
export interface ThumbDraft { id: string; name: string; settings: ThumbSettings; preview: string; createdAt: number; }

/** Settings that describe LOOK only — what "copy style" carries; content (title, subtitle text,
 *  tagline, chosen images) stays with each video. */
export type ThumbStyleOnly = Omit<ThumbSettings, 'title' | 'tagline' | 'subtitleText' | 'selectedIdxs'>;
export const extractThumbStyle = (s: ThumbSettings): ThumbStyleOnly => {
    const { title, tagline, subtitleText, selectedIdxs, ...style } = s;
    return style;
};

export const defaultThumbSettings = (o: { title: string; subtitleText: string; imageCount: number }): ThumbSettings => ({
    v: 1, thumbStyle: 'split', thumbPreset: 'amber', customColor: null, title: o.title, tagline: '',
    titleColor: DEFAULT_STYLE.titleColor, titleFontId: DEFAULT_STYLE.titleFontId, titleScale: DEFAULT_STYLE.titleScale,
    titleAlign: DEFAULT_STYLE.titleAlign, screenshotPlacement: DEFAULT_STYLE.screenshotPlacement, splitPlacement: DEFAULT_STYLE.splitPlacement,
    textEffectMode: DEFAULT_STYLE.textEffectMode,
    customShadow: { color: '#000000', opacity: 0.7, blur: 16, offsetX: 0, offsetY: 3 }, customShadowEnabled: true,
    customOutline: { color: '#000000', width: 2 }, customOutlineEnabled: false,
    accentColor: DEFAULT_STYLE.accentColor, accentWidth: DEFAULT_STYLE.accentWidth, splitRatio: DEFAULT_STYLE.splitRatio,
    leftBgColor: DEFAULT_STYLE.leftBgColor, bgSolidColor: DEFAULT_STYLE.bgSolidColor, imageFit: DEFAULT_STYLE.imageFit, focalPosition: DEFAULT_STYLE.focalPosition,
    cornerRadius: DEFAULT_STYLE.cornerRadius, watermarkEnabled: DEFAULT_STYLE.watermarkEnabled, watermarkOpacity: DEFAULT_STYLE.watermarkOpacity,
    subtitleText: o.subtitleText, subtitleColor: DEFAULT_STYLE.subtitleColor, subtitleScale: DEFAULT_STYLE.subtitleScale,
    bgBlur: DEFAULT_STYLE.bgBlur, bgDark: DEFAULT_STYLE.bgDark, textStripDark: DEFAULT_STYLE.textStripDark,
    selectedIdxs: Array.from({ length: Math.min(4, o.imageCount) }, (_, i) => i),
});

/** The single place that turns saved settings into generateThumbnail() parameters. */
export const settingsToGenParams = (s: ThumbSettings, o: { fallbackTitle: string; screenshotEls: HTMLImageElement[]; coverEl: HTMLImageElement | null }): GenThumbParams => {
    const font = THUMB_FONTS.find(f => f.id === s.titleFontId) || THUMB_FONTS[0];
    const custom = s.textEffectMode === 'custom';
    return {
        title: s.title || o.fallbackTitle,
        notebookTitle: s.subtitleText,
        presetId: s.thumbPreset,
        screenshotEls: o.screenshotEls,
        coverEl: o.coverEl,
        customColor: s.customColor,
        style: s.thumbStyle,
        tagline: s.tagline,
        subtitleColor: s.subtitleColor,
        subtitleScale: s.subtitleScale,
        bgBlur: s.bgBlur,
        bgDark: s.bgDark,
        textStripDark: s.textStripDark,
        titleStyle: {
            color: s.titleColor,
            fontFamily: font.family,
            scale: s.titleScale,
            alignment: isOverlayStyle(s.thumbStyle) ? s.titleAlign : 'left',
            placement: isOverlayStyle(s.thumbStyle) ? s.screenshotPlacement : s.splitPlacement,
            textEffect: {
                mode: s.textEffectMode,
                shadow: custom ? { ...s.customShadow, opacity: s.customShadowEnabled ? s.customShadow.opacity : 0 } : undefined,
                outline: custom ? { ...s.customOutline, width: s.customOutlineEnabled ? s.customOutline.width : 0 } : undefined,
            },
        },
        accentStyle: { color: s.accentColor ?? DEFAULT_ACCENT, width: s.accentWidth },
        splitRatio: s.splitRatio,
        leftBgColor: s.leftBgColor,
        bgSolidColor: s.bgSolidColor,
        imageStyle: { fit: s.imageFit, focalPosition: s.focalPosition, cornerRadius: s.cornerRadius, zoom: s.imageZoom ?? 0, focalX: s.focalX, focalY: s.focalY, fakeStack: s.fakeStack },
        watermark: { enabled: s.watermarkEnabled, opacity: s.watermarkOpacity },
        penStrokes: s.penStrokes ?? [],
    };
};

const loadImageElement = (url: string): Promise<HTMLImageElement | null> => new Promise(res => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = url;
});

/** Renders the full-size thumbnail (data URL) for saved settings, without any editor UI. */
export async function renderThumbnailFromSettings(
    s: ThumbSettings,
    ctx: { noteImages: { src: string; label: string }[]; notebookId: string; fallbackTitle: string },
): Promise<string> {
    const font = THUMB_FONTS.find(f => f.id === s.titleFontId) || THUMB_FONTS[0];
    const urls = s.selectedIdxs.filter(i => Number.isInteger(i) && i >= 0 && i < ctx.noteImages.length).slice(0, 4).map(i => ctx.noteImages[i].src);
    let cover: string | null = null;
    try { cover = localStorage.getItem(`yt_book_cover_${ctx.notebookId}`) || null; } catch { /* ignore */ }
    const [, ...loaded] = await Promise.all([
        ensureFontReady(font.family),
        ...urls.map(loadImageElement),
        cover ? loadImageElement(cover) : Promise.resolve(null),
    ]);
    const screenshotEls = loaded.slice(0, urls.length).filter(Boolean) as HTMLImageElement[];
    const coverEl = cover ? ((loaded[loaded.length - 1] ?? null) as HTMLImageElement | null) : null;
    return generateThumbnail(settingsToGenParams(s, { fallbackTitle: ctx.fallbackTitle, screenshotEls, coverEl }));
}

/** Small JPEG copy of a thumbnail for lists and comparisons. */
export const downscaleToJpeg = (dataUrl: string, w = 288, h = 162): Promise<string> => new Promise(res => {
    const img = new Image();
    img.onload = () => {
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d')!.drawImage(img, 0, 0, w, h);
        res(c.toDataURL('image/jpeg', 0.65));
    };
    img.onerror = () => res('');
    img.src = dataUrl;
});

export function ThumbnailEditorModal({
    title,
    notebookTitle,
    noteImages,
    notebookId,
    language,
    youtubeVideoId,
    ytConnected,
    onClose,
    onThumbnailChange,
    noteContent,
    noteImageBase64,
    noteImageMimeType,
    initialSettings,
    settingsSavedAt,
    onPushed,
    onSettingsChange,
    drafts,
    onSaveDraft,
    onDeleteDraft,
    liveThumbUrl,
    tabs,
}: ThumbnailEditorProps) {
    const vn = language === 'vietnamese';
    const S: Partial<ThumbSettings> = initialSettings ?? {};
    // Captured once: the banner describes where this editing session started, not later pushes.
    const startedFromRef = useRef<number | undefined>(initialSettings && settingsSavedAt ? settingsSavedAt : undefined);

    // Layout / content
    const [thumbStyle, setThumbStyle] = useState<ThumbStyle>(S.thumbStyle ?? (noteImages.length === 0 ? 'title' : 'split'));
    const [thumbPreset, setThumbPreset] = useState<ThumbPresetId>(S.thumbPreset ?? 'amber');
    const [customColor, setCustomColor] = useState<string | null>(S.customColor ?? null);
    const [thumbTitle, setThumbTitle] = useState(S.title ?? title);
    const [isGenTitle, setIsGenTitle] = useState(false);
    const handleGenTitle = async () => {
        setIsGenTitle(true);
        try {
            const aiTitle = await generateTitleForNode(noteContent || '', noteImageBase64 || null, noteImageMimeType || null, language, 'thumbnail');
            if (aiTitle) setThumbTitle(aiTitle);
        } catch { /* non-fatal — user can still type/edit the title manually */ }
        finally { setIsGenTitle(false); }
    };
    const [thumbTagline, setThumbTagline] = useState(S.tagline ?? '');

    // Title style
    const [titleColor, setTitleColor] = useState(S.titleColor ?? DEFAULT_STYLE.titleColor);
    const [titleFontId, setTitleFontId] = useState(S.titleFontId ?? DEFAULT_STYLE.titleFontId);
    const [titleScale, setTitleScale] = useState(S.titleScale ?? DEFAULT_STYLE.titleScale);
    const [titleAlign, setTitleAlign] = useState<TitleAlign>(S.titleAlign ?? DEFAULT_STYLE.titleAlign); // screenshot mode only — split's left-anchor is layout-fixed
    const [screenshotPlacement, setScreenshotPlacement] = useState<TitlePlacement>(S.screenshotPlacement ?? DEFAULT_STYLE.screenshotPlacement);
    const [splitPlacement, setSplitPlacement] = useState<TitlePlacement>(S.splitPlacement ?? DEFAULT_STYLE.splitPlacement);
    const [textEffectMode, setTextEffectModeRaw] = useState<TextEffectMode>(S.textEffectMode ?? DEFAULT_STYLE.textEffectMode);
    const [customShadow, setCustomShadow] = useState<ShadowConfig>(S.customShadow ?? { color: '#000000', opacity: 0.7, blur: 16, offsetX: 0, offsetY: 3 });
    const [customShadowEnabled, setCustomShadowEnabled] = useState(S.customShadowEnabled ?? true);
    const [customOutline, setCustomOutline] = useState<OutlineConfig>(S.customOutline ?? { color: '#000000', width: 2 });
    const [customOutlineEnabled, setCustomOutlineEnabled] = useState(S.customOutlineEnabled ?? false);

    // Accent
    const [accentColor, setAccentColor] = useState<string | null>(S.accentColor ?? DEFAULT_STYLE.accentColor);
    const [accentWidth, setAccentWidth] = useState(S.accentWidth ?? DEFAULT_STYLE.accentWidth);
    const [splitRatio, setSplitRatio] = useState(S.splitRatio ?? DEFAULT_STYLE.splitRatio);
    const [leftBgColor, setLeftBgColor] = useState<string | null>(S.leftBgColor ?? DEFAULT_STYLE.leftBgColor);
    // UI-only convenience toggle (not persisted) — "linked" just means the same flat-color picker
    // drives both leftBgColor and customColor at once, for when you don't want two separate pickers.
    const [splitColorLinked, setSplitColorLinked] = useState(false);
    const [bgSolidColor, setBgSolidColor] = useState<string | null>(S.bgSolidColor ?? DEFAULT_STYLE.bgSolidColor);

    // Image
    const [imageFit, setImageFit] = useState<ImageFit>(S.imageFit ?? DEFAULT_STYLE.imageFit);
    const [focalPosition, setFocalPosition] = useState<FocalPosition>(S.focalPosition ?? DEFAULT_STYLE.focalPosition);
    const [imageZoom, setImageZoom] = useState(S.imageZoom ?? 0);
    const [focalX, setFocalX] = useState<number | undefined>(S.focalX);
    const [focalY, setFocalY] = useState<number | undefined>(S.focalY);
    const [fakeStack, setFakeStack] = useState(S.fakeStack ?? false);
    const [penStrokes, setPenStrokes] = useState<PenStroke[]>(S.penStrokes ?? []);
    const [penMode, setPenMode] = useState(false);
    const [penColor, setPenColor] = useState(PEN_COLORS[0]);
    const [currentStroke, setCurrentStroke] = useState<PenStroke | null>(null);
    const previewImgRef = useRef<HTMLDivElement>(null);
    const focalPreviewRef = useRef<HTMLDivElement>(null);
    const [focalPreviewRatio, setFocalPreviewRatio] = useState<number | null>(null);
    const [cornerRadius, setCornerRadius] = useState<CornerRadiusSize>(S.cornerRadius ?? DEFAULT_STYLE.cornerRadius);

    // Watermark
    const [watermarkEnabled, setWatermarkEnabled] = useState(S.watermarkEnabled ?? DEFAULT_STYLE.watermarkEnabled);
    const [watermarkOpacity, setWatermarkOpacity] = useState(S.watermarkOpacity ?? DEFAULT_STYLE.watermarkOpacity);

    // Subtitle / background (screenshot mode)
    const [thumbSubtitleText, setThumbSubtitleText] = useState(S.subtitleText ?? notebookTitle);
    const [thumbSubtitleColor, setThumbSubtitleColor] = useState(S.subtitleColor ?? DEFAULT_STYLE.subtitleColor);
    const [thumbSubtitleScale, setThumbSubtitleScale] = useState(S.subtitleScale ?? DEFAULT_STYLE.subtitleScale);
    const [thumbBgBlur, setThumbBgBlur] = useState(S.bgBlur ?? DEFAULT_STYLE.bgBlur);
    const [thumbBgDark, setThumbBgDark] = useState(S.bgDark ?? DEFAULT_STYLE.bgDark);
    const [thumbTextStripDark, setThumbTextStripDark] = useState(S.textStripDark ?? DEFAULT_STYLE.textStripDark);

    // Auto never overwrites Custom; switching into Custom seeds it from what Auto was just showing.
    const setTextEffectMode = (mode: TextEffectMode) => {
        if (mode === 'custom' && textEffectMode !== 'custom') {
            const preset = THUMB_PRESETS.find(p => p.id === thumbPreset) || THUMB_PRESETS[0];
            const bgFrom = customColor ?? preset.from, bgTo = customColor ?? preset.to;
            const surface = isOverlayStyle(thumbStyle) ? scrimSurfaceColor(thumbTextStripDark) : mixHex(bgFrom, bgTo, 0.5);
            const scrim = isOverlayStyle(thumbStyle) ? thumbTextStripDark : 0;
            const auto = computeAutoTextEffect(titleColor, surface, scrim);
            setCustomShadow(auto.shadow); setCustomShadowEnabled(auto.shadow.opacity > 0);
            setCustomOutline(auto.outline); setCustomOutlineEnabled(auto.outline.width > 0);
        }
        setTextEffectModeRaw(mode);
    };

    const handleResetToPreset = () => {
        setTitleColor(DEFAULT_STYLE.titleColor);
        setTitleFontId(DEFAULT_STYLE.titleFontId);
        setTitleScale(DEFAULT_STYLE.titleScale);
        setTitleAlign(DEFAULT_STYLE.titleAlign);
        setScreenshotPlacement(DEFAULT_STYLE.screenshotPlacement);
        setSplitPlacement(DEFAULT_STYLE.splitPlacement);
        setTextEffectModeRaw(DEFAULT_STYLE.textEffectMode);
        setAccentColor(DEFAULT_STYLE.accentColor);
        setAccentWidth(DEFAULT_STYLE.accentWidth);
        setSplitRatio(DEFAULT_STYLE.splitRatio);
        setLeftBgColor(DEFAULT_STYLE.leftBgColor);
        setBgSolidColor(DEFAULT_STYLE.bgSolidColor);
        setImageFit(DEFAULT_STYLE.imageFit);
        setFocalPosition(DEFAULT_STYLE.focalPosition);
        setImageZoom(0);
        setFocalX(undefined);
        setFocalY(undefined);
        setFakeStack(false);
        setPenStrokes([]);
        setCornerRadius(DEFAULT_STYLE.cornerRadius);
        setWatermarkEnabled(DEFAULT_STYLE.watermarkEnabled);
        setWatermarkOpacity(DEFAULT_STYLE.watermarkOpacity);
        setThumbSubtitleText(notebookTitle);
        setThumbSubtitleColor(DEFAULT_STYLE.subtitleColor);
        setThumbSubtitleScale(DEFAULT_STYLE.subtitleScale);
        setThumbBgBlur(DEFAULT_STYLE.bgBlur);
        setThumbBgDark(DEFAULT_STYLE.bgDark);
        setThumbTextStripDark(DEFAULT_STYLE.textStripDark);
    };

    // Image selection — Set of indices into noteImages (max 4)
    const restoredIdxs = (S.selectedIdxs ?? []).filter(i => Number.isInteger(i) && i >= 0 && i < noteImages.length).slice(0, 4);
    const initIdxs = restoredIdxs.length > 0
        ? new Set(restoredIdxs)
        : (noteImages.length > 0 ? new Set(noteImages.map((_, i) => i).slice(0, 4)) : new Set<number>());
    const [selectedIdxs, setSelectedIdxs] = useState<Set<number>>(initIdxs);

    // Book cover (persisted per notebook)
    const COVER_KEY = `yt_book_cover_${notebookId}`;
    const [coverDataUrl, setCoverDataUrl] = useState<string | null>(() => {
        try { return localStorage.getItem(COVER_KEY) || null; } catch { return null; }
    });
    const saveCover = (url: string | null) => {
        setCoverDataUrl(url);
        try { url ? localStorage.setItem(COVER_KEY, url) : localStorage.removeItem(COVER_KEY); } catch {}
    };

    // Saved color combos (bg + accent + left panel + title/subtitle text color) — shared across
    // every notebook, since this is a personal style preference, not per-note data.
    const COMBO_KEY = 'sb_thumb_saved_combos';
    const [savedCombos, setSavedCombos] = useState<ColorCombo[]>(() => {
        try { return JSON.parse(localStorage.getItem(COMBO_KEY) || '[]'); } catch { return []; }
    });
    const persistCombos = (combos: ColorCombo[]) => {
        setSavedCombos(combos);
        try { localStorage.setItem(COMBO_KEY, JSON.stringify(combos)); } catch {}
    };
    const handleSaveCombo = () => {
        const combo: ColorCombo = {
            id: `${Date.now()}`,
            bg: customColor ?? THUMB_PRESETS.find(p => p.id === thumbPreset)?.from ?? '#92400e',
            accent: accentColor ?? DEFAULT_ACCENT,
            leftBg: leftBgColor,
            titleColor,
            subtitleColor: thumbSubtitleColor,
        };
        persistCombos([...savedCombos, combo].slice(-10));
    };
    const handleApplyCombo = (combo: ColorCombo) => {
        setCustomColor(combo.bg);
        setAccentColor(combo.accent);
        setLeftBgColor(combo.leftBg);
        setTitleColor(combo.titleColor);
        setThumbSubtitleColor(combo.subtitleColor);
    };
    const handleDeleteCombo = (id: string) => {
        persistCombos(savedCombos.filter(c => c.id !== id));
    };

    const buildSettings = (): ThumbSettings => ({
        v: 1, thumbStyle, thumbPreset, customColor, title: thumbTitle, tagline: thumbTagline,
        titleColor, titleFontId, titleScale, titleAlign, screenshotPlacement, splitPlacement, textEffectMode,
        customShadow, customShadowEnabled, customOutline, customOutlineEnabled,
        accentColor, accentWidth, splitRatio, leftBgColor, bgSolidColor, imageFit, focalPosition, imageZoom, focalX, focalY, fakeStack, cornerRadius,
        watermarkEnabled, watermarkOpacity,
        subtitleText: thumbSubtitleText, subtitleColor: thumbSubtitleColor, subtitleScale: thumbSubtitleScale,
        bgBlur: thumbBgBlur, bgDark: thumbBgDark, textStripDark: thumbTextStripDark,
        selectedIdxs: [...selectedIdxs],
        penStrokes,
    });
    // No thumbnailSettings were ever saved for this video (e.g. its current YouTube thumbnail came
    // from somewhere other than this editor) — the very first render should show what's actually
    // live on YouTube as the starting point, not silently synthesize a brand-new default composition
    // that has nothing to do with it. See handleSave — nothing here is "applied" until that fires.
    const skipFirstGenRef = useRef(!(initialSettings && Object.keys(initialSettings).length > 0) && !!liveThumbUrl);
    // A manually uploaded image (or, initially, the live YouTube image above) replaces the generated
    // preview without touching the settings, so a push right after it must not record the settings
    // as "what's on YouTube".
    const customImageActiveRef = useRef(skipFirstGenRef.current);

    // Preview + caching
    const [thumbPreview, setThumbPreview] = useState(() => skipFirstGenRef.current ? liveThumbUrl! : '');
    const [isUploading, setIsUploading] = useState(false);
    const [thumbMsg, setThumbMsg] = useState('');
    const imgCacheRef = useRef<Map<string, HTMLImageElement>>(new Map());

    // File inputs
    const customImgRef = useRef<HTMLInputElement>(null);
    const coverInputRef = useRef<HTMLInputElement>(null);
    const colorPickerRef = useRef<HTMLInputElement>(null);
    const accentColorPickerRef = useRef<HTMLInputElement>(null);
    const previewContainerRef = useRef<HTMLDivElement>(null);

    // Drag-to-resize the split divider — clamped to the same [0.15, 0.5] range the renderer uses,
    // so the left screenshot panel can shrink but never grow past the original equal-half split.
    const handleDividerPointerDown = (e: React.PointerEvent) => {
        e.preventDefault();
        const container = previewContainerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const onMove = (ev: PointerEvent) => {
            const ratio = Math.min(0.5, Math.max(0.15, (ev.clientX - rect.left) / rect.width));
            setSplitRatio(ratio);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    // Freehand pen stroke, captured directly on the live preview — normalized 0-1 to the preview's
    // own box, which is exactly the final 1280x720 canvas (same aspect ratio), so no extra mapping.
    const startPenStroke = (e: React.PointerEvent) => {
        if (!penMode) return;
        e.preventDefault();
        const container = previewContainerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const toFrac = (clientX: number, clientY: number) => ({
            x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
            y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
        });
        const stroke: PenStroke = { id: `pen_${Date.now()}`, color: penColor, points: [toFrac(e.clientX, e.clientY)] };
        setCurrentStroke(stroke);
        const onMove = (ev: PointerEvent) => {
            setCurrentStroke(s => s ? { ...s, points: [...s.points, toFrac(ev.clientX, ev.clientY)] } : s);
        };
        const onUp = () => {
            window.removeEventListener('pointermove', onMove);
            window.removeEventListener('pointerup', onUp);
            setCurrentStroke(s => {
                if (s && s.points.length >= 2) setPenStrokes(prev => [...prev, s]);
                return null;
            });
        };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };

    const loadImg = (url: string): Promise<HTMLImageElement | null> => {
        const cached = imgCacheRef.current.get(url);
        if (cached) return Promise.resolve(cached);
        return new Promise(res => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => { imgCacheRef.current.set(url, img); res(img); };
            img.onerror = () => res(null);
            img.src = url;
        });
    };

    const selectedFont = THUMB_FONTS.find(f => f.id === titleFontId) || THUMB_FONTS[0];

    // Rebuild thumbnail whenever any dependency changes.
    //
    // onThumbnailChange/onSettingsChange are only auto-fired here when there's no liveThumbUrl —
    // i.e. this video doesn't exist on YouTube yet (YouTubeUploadModal's pre-publish flow), so
    // there's nothing "live" that a synthesized preview could wrongly clobber; every edit should
    // keep reflecting live there, exactly as before this change.
    //
    // When liveThumbUrl IS given (YouTubeManagerModal, editing an already-published video's
    // thumbnail), these are deliberately NOT auto-fired: they used to fire on every regeneration —
    // including the very first, on mere mount — silently "applying" whatever this effect produced
    // everywhere the app shows this video's thumbnail before the user asked for anything. There,
    // they now fire only from handleSave, when the user explicitly clicks Save.
    const selectedKey = [...selectedIdxs].sort().join(',');
    useEffect(() => {
        let cancelled = false;
        if (skipFirstGenRef.current) {
            // Nothing saved yet for this video — leave the live-YouTube-image preview (already set
            // as thumbPreview's initial state) alone until the user actually changes something.
            skipFirstGenRef.current = false;
            return;
        }
        const selectedUrls = [...selectedIdxs].slice(0, 4).map(i => noteImages[i]?.src).filter(Boolean) as string[];
        Promise.all([
            ensureFontReady(selectedFont.family),
            ...selectedUrls.map(loadImg),
            coverDataUrl ? loadImg(coverDataUrl) : Promise.resolve(null),
        ]).then(([, ...results]) => {
            if (cancelled) return;
            const ssEls = results.slice(0, selectedUrls.length).filter(Boolean) as HTMLImageElement[];
            const covEl = (results[results.length - 1] ?? null) as HTMLImageElement | null;
            const dataUrl = generateThumbnail(settingsToGenParams(buildSettings(), { fallbackTitle: title, screenshotEls: ssEls, coverEl: coverDataUrl ? covEl : null }));
            customImageActiveRef.current = false;
            setThumbPreview(dataUrl);
            if (!liveThumbUrl) {
                onThumbnailChange?.(dataUrl);
                onSettingsChange?.(buildSettings());
            }
        });
        return () => { cancelled = true; };
    }, [selectedKey, coverDataUrl, thumbPreset, customColor, thumbStyle, thumbTitle, thumbTagline,
        titleColor, titleFontId, titleScale, titleAlign, screenshotPlacement, splitPlacement,
        textEffectMode, customShadow, customShadowEnabled, customOutline, customOutlineEnabled,
        accentColor, accentWidth, splitRatio, leftBgColor, bgSolidColor, imageFit, focalPosition, imageZoom, focalX, focalY, fakeStack, penStrokes, cornerRadius, watermarkEnabled, watermarkOpacity,
        thumbSubtitleText, thumbSubtitleColor, thumbSubtitleScale, thumbBgBlur, thumbBgDark, thumbTextStripDark]);

    const handleDownload = () => {
        if (!thumbPreview) return;
        const a = document.createElement('a');
        a.href = thumbPreview;
        a.download = `thumb_${(title || 'thumbnail').replace(/[^a-z0-9]+/gi, '_').toLowerCase().slice(0, 50)}.png`;
        a.click();
    };

    const handleCustomImage = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0];
        if (!f) return;
        const reader = new FileReader();
        reader.onload = ev => {
            const dataUrl = ev.target?.result as string;
            customImageActiveRef.current = true;
            setThumbPreview(dataUrl);
            onThumbnailChange?.(dataUrl);
            onSettingsChange?.(null);
        };
        reader.readAsDataURL(f);
        e.target.value = '';
    };

    // ── Drafts (saved versions to compare later) ────────────────────────────────────────────────
    const MAX_DRAFTS = 6;
    const [showCompare, setShowCompare] = useState(false);
    const [draftMsg, setDraftMsg] = useState('');
    const [busyCardId, setBusyCardId] = useState<string | null>(null);
    const [cardMsg, setCardMsg] = useState('');

    // Loads a saved version's settings into every editor control.
    const applySettingsToState = (v: ThumbSettings) => {
        // Not a template click by default (loading a draft/live/initial snapshot) — applyTemplate
        // overrides this right after calling in, so its own click still shows as active.
        setActiveTemplateId(null);
        setThumbStyle(v.thumbStyle); setThumbPreset(v.thumbPreset); setCustomColor(v.customColor);
        setThumbTitle(v.title); setThumbTagline(v.tagline);
        setTitleColor(v.titleColor); setTitleFontId(v.titleFontId); setTitleScale(v.titleScale); setTitleAlign(v.titleAlign);
        setScreenshotPlacement(v.screenshotPlacement); setSplitPlacement(v.splitPlacement); setTextEffectModeRaw(v.textEffectMode);
        setCustomShadow(v.customShadow); setCustomShadowEnabled(v.customShadowEnabled);
        setCustomOutline(v.customOutline); setCustomOutlineEnabled(v.customOutlineEnabled);
        setAccentColor(v.accentColor); setAccentWidth(v.accentWidth); setSplitRatio(v.splitRatio); setLeftBgColor(v.leftBgColor);
        setBgSolidColor(v.bgSolidColor ?? null);
        setImageFit(v.imageFit); setFocalPosition(v.focalPosition); setImageZoom(v.imageZoom ?? 0);
        setFocalX(v.focalX); setFocalY(v.focalY);
        setFakeStack(v.fakeStack ?? false); setPenStrokes(v.penStrokes ?? []);
        setCornerRadius(v.cornerRadius);
        setWatermarkEnabled(v.watermarkEnabled); setWatermarkOpacity(v.watermarkOpacity);
        setThumbSubtitleText(v.subtitleText); setThumbSubtitleColor(v.subtitleColor); setThumbSubtitleScale(v.subtitleScale);
        setThumbBgBlur(v.bgBlur); setThumbBgDark(v.bgDark); setThumbTextStripDark(v.textStripDark);
        setSelectedIdxs(new Set(v.selectedIdxs.filter(i => i >= 0 && i < noteImages.length).slice(0, 4)));
    };

    // Quick-apply a curated "look" — changes only style/layout fields, never the content (title,
    // subtitle text, tagline, chosen images) NOR the zoom/focal point, so switching templates never
    // wipes out what the user already typed or already framed. Down to 2 templates (Split /
    // Top-bottom) per explicit feedback that "Highlight Zoom" was just the top-bottom layout at a
    // different zoom level, not a genuinely distinct 3rd look — zoom is a universal control on the
    // image area now, available under either template (see the Zoom slider below).
    const [activeTemplateId, setActiveTemplateId] = useState<'topbottom' | 'split' | 'title' | null>(null);
    const applyTemplate = (id: 'topbottom' | 'split' | 'title', overrides: Partial<ThumbStyleOnly>) => {
        applySettingsToState({ ...buildSettings(), ...overrides });
        setActiveTemplateId(id);
    };
    const applyTopBottomTemplate = () => applyTemplate('topbottom', {
        thumbStyle: 'screenshot', screenshotPlacement: 'bottom',
        imageFit: 'fill', cornerRadius: 'medium',
        bgBlur: 10, bgDark: 0.42, textStripDark: 0.75,
    });
    const applyTitleTemplate = () => applyTemplate('title', {
        thumbStyle: 'title', screenshotPlacement: 'bottom', titleAlign: 'left',
        bgBlur: 10, bgDark: 0.35, textStripDark: 0.6,
    });
    const applySplitTemplate = () => applyTemplate('split', {
        thumbStyle: 'split', splitPlacement: 'center', splitRatio: 0.42,
        leftBgColor: null, imageFit: 'fit', cornerRadius: 'medium',
    });

    // "Lưu" (Save): the one place onThumbnailChange/onSettingsChange are called now — this is what
    // actually applies the current in-editor preview to wherever the app shows this video's
    // thumbnail (previously happened automatically on every edit, which is the bug being fixed).
    // Also saves a draft snapshot at the same time, so there's something to compare back against
    // later — a full drafts list only skips the draft part, it doesn't block applying.
    const handleSave = async () => {
        setDraftMsg('');
        if (!thumbPreview || customImageActiveRef.current) {
            setDraftMsg(vn ? 'Chưa chỉnh sửa gì để lưu — đây vẫn là ảnh gốc trên YouTube.' : "Nothing edited yet to save — this is still YouTube's own image.");
            return;
        }
        const settings = buildSettings();
        onThumbnailChange?.(thumbPreview);
        onSettingsChange?.(settings);
        let msg = vn ? '✓ Đã lưu và áp dụng' : '✓ Saved and applied';
        if (onSaveDraft) {
            if ((drafts?.length ?? 0) >= MAX_DRAFTS) {
                msg += vn ? ` (chưa lưu nháp — tối đa ${MAX_DRAFTS} bản, xóa bớt trong "So sánh")` : ` (draft not saved — at most ${MAX_DRAFTS}, delete one in "Compare")`;
            } else {
                try {
                    const preview = await downscaleToJpeg(thumbPreview);
                    const nextNum = Math.max(0, ...(drafts || []).map(d => Number(/(\d+)$/.exec(d.name)?.[1] ?? 0))) + 1;
                    await onSaveDraft({ id: `d${Date.now().toString(36)}`, name: `${vn ? 'Nháp' : 'Draft'} ${nextNum}`, settings, preview, createdAt: Date.now() });
                } catch (e: any) {
                    msg = `${vn ? 'Đã áp dụng, nhưng lỗi khi lưu nháp' : 'Applied, but draft save failed'}: ${e?.message || e}`;
                }
            }
        }
        setDraftMsg(msg);
    };

    const renderFull = (v: ThumbSettings) => renderThumbnailFromSettings(v, { noteImages, notebookId, fallbackTitle: title });

    const pushSettings = async (id: string, v: ThumbSettings) => {
        if (!youtubeVideoId || !ytConnected) return;
        setBusyCardId(id); setCardMsg('');
        try {
            const url = await renderFull(v);
            const [, mime, b64] = url.match(/^data:([^;]+);base64,(.+)$/) ?? [];
            await updateYouTubeVideo(youtubeVideoId, { thumbnailBase64: b64, thumbnailMime: mime });
            onPushed?.(v);
            applySettingsToState(v);
            onThumbnailChange?.(url); // same fix as handlePushToYouTube — keep the outside preview in sync
            setCardMsg(vn ? '✓ Đã đặt làm thumbnail đang dùng trên YouTube' : '✓ Set as the live YouTube thumbnail');
        } catch (e: any) {
            setCardMsg(`${vn ? 'Lỗi' : 'Failed'}: ${e?.message === 'not_connected' ? (vn ? 'YouTube chưa kết nối hoặc hết phiên' : 'YouTube not connected or session expired') : (e?.message || e)}`);
        } finally { setBusyCardId(null); }
    };

    const downloadSettings = async (id: string, v: ThumbSettings, name: string) => {
        setBusyCardId(id); setCardMsg('');
        try {
            const url = await renderFull(v);
            const a = document.createElement('a');
            a.href = url;
            a.download = `thumb_${(title || 'thumbnail').replace(/[^a-z0-9]+/gi, '_').toLowerCase().slice(0, 40)}_${name.replace(/[^a-z0-9]+/gi, '_').toLowerCase()}.png`;
            a.click();
        } catch (e: any) { setCardMsg(`${vn ? 'Lỗi' : 'Failed'}: ${e?.message || e}`); }
        finally { setBusyCardId(null); }
    };

    const handlePushToYouTube = async () => {
        if (!youtubeVideoId || !ytConnected || !thumbPreview) return;
        setIsUploading(true); setThumbMsg('');
        try {
            const [, mime, b64] = thumbPreview.match(/^data:([^;]+);base64,(.+)$/) ?? [];
            await updateYouTubeVideo(youtubeVideoId, { thumbnailBase64: b64, thumbnailMime: mime });
            if (!customImageActiveRef.current) onPushed?.(buildSettings());
            // Tell the parent what's now actually live on YouTube — otherwise its own preview box
            // (outside this modal) has no idea a push just happened and keeps trying to load
            // YouTube's hqdefault.jpg, which can take a while to reflect a brand-new custom
            // thumbnail. Pushing without ever clicking "Lưu" left that box stuck showing YouTube's
            // (possibly still-black) old default indefinitely.
            onThumbnailChange?.(thumbPreview);
            setThumbMsg(vn ? '✓ Thumbnail đã cập nhật!' : '✓ Thumbnail updated!');
        } catch (e: any) {
            setThumbMsg(`${vn ? 'Lỗi cập nhật thumbnail' : 'Failed to update thumbnail'}: ${e?.message === 'not_connected' ? (vn ? 'YouTube chưa kết nối hoặc hết phiên' : 'YouTube not connected or session expired') : (e?.message || e)}`);
        } finally { setIsUploading(false); }
    };

    const toggleImage = (idx: number) => {
        setSelectedIdxs(prev => {
            const next = new Set(prev);
            if (next.has(idx)) {
                next.delete(idx);
            } else if (next.size < 4) {
                next.add(idx);
            }
            return next;
        });
    };

    const orderedIdxs = [...selectedIdxs]; // insertion order
    const hasImageForCrop = selectedIdxs.size >= 1 || !!coverDataUrl;
    const focalPreviewUrl = orderedIdxs.length > 0 ? noteImages[orderedIdxs[0]]?.src : coverDataUrl || undefined;

    // Natural aspect ratio of the image being focal-positioned, so the drag preview box below
    // shows it edge-to-edge (no letterboxing) and mouse-position -> fraction math stays exact.
    useEffect(() => {
        if (!focalPreviewUrl) { setFocalPreviewRatio(null); return; }
        let cancelled = false;
        const img = new Image();
        img.onload = () => { if (!cancelled) setFocalPreviewRatio((img.naturalWidth || 1) / (img.naturalHeight || 1)); };
        img.src = focalPreviewUrl;
        return () => { cancelled = true; };
    }, [focalPreviewUrl]);

    // Drag (or click) anywhere on the preview to set the focal point continuously, instead of
    // picking from 9 fixed spots — precise enough to center on a specific formula.
    const startFocalDrag = (e: React.PointerEvent) => {
        e.preventDefault();
        const el = focalPreviewRef.current;
        if (!el) return;
        const apply = (clientX: number, clientY: number) => {
            const rect = el.getBoundingClientRect();
            setFocalX(Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)));
            setFocalY(Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)));
        };
        apply(e.clientX, e.clientY);
        const onMove = (ev: PointerEvent) => apply(ev.clientX, ev.clientY);
        const onUp = () => { window.removeEventListener('pointermove', onMove); window.removeEventListener('pointerup', onUp); };
        window.addEventListener('pointermove', onMove);
        window.addEventListener('pointerup', onUp);
    };
    const focalDisplayX = focalX ?? FOCAL_FRACTIONS[focalPosition][0];
    const focalDisplayY = focalY ?? FOCAL_FRACTIONS[focalPosition][1];
    const placement = isOverlayStyle(thumbStyle) ? screenshotPlacement : splitPlacement;
    const setPlacement = isOverlayStyle(thumbStyle) ? setScreenshotPlacement : setSplitPlacement;

    return (
        <div className={`fixed inset-0 ${tabs ? 'z-[320] bg-black/60' : 'z-[500] bg-black/70'} flex items-center justify-center p-4`}
            onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className={`bg-white dark:bg-slate-900 rounded-2xl ${tabs ? 'w-full max-w-[1280px] h-[94vh]' : 'w-[880px] max-w-full max-h-[92vh]'} flex flex-col shadow-2xl overflow-hidden`}>

                {/* Header */}
                <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-slate-800 flex-shrink-0">
                    <h3 className="font-bold text-sm text-gray-900 dark:text-white flex items-center gap-2">
                        <ImageIcon size={14} className="text-red-400" />
                        {vn ? 'Chỉnh sửa thumbnail' : 'Thumbnail Editor'}
                    </h3>
                    {tabs && <div className="ml-auto mr-3">{tabs}</div>}
                    <button onClick={onClose} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors">
                        <X size={14} className="text-gray-400" />
                    </button>
                </div>

                <div className="flex flex-1 overflow-hidden min-h-0">

                    {/* Left panel: preview + actions + image picker */}
                    <div className="flex-1 p-4 flex flex-col gap-3 overflow-y-auto hide-scrollbar">

                        {/* Preview */}
                        <div ref={previewContainerRef} className="relative rounded-xl overflow-hidden border border-gray-200 dark:border-slate-700 shadow-sm bg-gray-100 dark:bg-slate-800" style={{ aspectRatio: '16/9' }}>
                            {thumbPreview
                                ? <img src={thumbPreview} alt="Thumbnail preview" className="w-full h-full object-cover" />
                                : <div className="w-full h-full flex items-center justify-center"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
                            }
                            {thumbStyle === 'split' && selectedIdxs.size > 0 && !penMode && (
                                <div
                                    onPointerDown={handleDividerPointerDown}
                                    title={vn ? 'Kéo để chỉnh tỉ lệ ảnh/chữ' : 'Drag to resize image vs. text'}
                                    className="absolute top-0 bottom-0 w-4 -ml-2 cursor-ew-resize group flex items-center justify-center touch-none"
                                    style={{ left: `${splitRatio * 100}%` }}
                                >
                                    <div className="w-1 h-10 rounded-full bg-white/60 group-hover:bg-white group-active:bg-white shadow-md transition-colors" />
                                </div>
                            )}
                            {/* Pen tool — draws directly on the live (already-composited) preview; see PenStroke's
                                doc comment for why this is canvas-space rather than image-space. */}
                            {penMode && (
                                <svg
                                    onPointerDown={startPenStroke}
                                    viewBox="0 0 100 100" preserveAspectRatio="none"
                                    className="absolute inset-0 w-full h-full cursor-crosshair"
                                >
                                    {penStrokes.map(s => (
                                        <polyline key={s.id} points={s.points.map(p => `${p.x * 100},${p.y * 100}`).join(' ')}
                                            fill="none" stroke={s.color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                                    ))}
                                    {currentStroke && (
                                        <polyline points={currentStroke.points.map(p => `${p.x * 100},${p.y * 100}`).join(' ')}
                                            fill="none" stroke={currentStroke.color} strokeWidth={2.2} strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                                    )}
                                </svg>
                            )}
                        </div>

                        {/* Pen toolbar */}
                        <div className="flex items-center gap-2 flex-wrap">
                            <button onClick={() => setPenMode(v => !v)}
                                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg border transition-colors ${penMode ? 'border-red-400 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400' : 'border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-gray-400'}`}>
                                ✏️ {penMode ? (vn ? 'Đang vẽ' : 'Drawing') : (vn ? 'Vẽ' : 'Draw')}
                            </button>
                            {penMode && (
                                <>
                                    <div className="flex items-center gap-1">
                                        {PEN_COLORS.map(c => (
                                            <button key={c} onClick={() => setPenColor(c)}
                                                className={`w-5 h-5 rounded-full border-2 ${penColor === c ? 'border-blue-500' : 'border-gray-200 dark:border-slate-600'}`}
                                                style={{ backgroundColor: c }} />
                                        ))}
                                    </div>
                                    <button onClick={() => setPenStrokes(s => s.slice(0, -1))} disabled={penStrokes.length === 0}
                                        className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 disabled:opacity-30">
                                        ↩ {vn ? 'Hoàn tác' : 'Undo'}
                                    </button>
                                </>
                            )}
                        </div>

                        {/* Actions */}
                        <div className="flex flex-wrap gap-2">
                            <button onClick={handleDownload} disabled={!thumbPreview}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-gray-300 hover:border-gray-400 disabled:opacity-40 transition-colors">
                                <Download size={12} /> {vn ? 'Tải PNG' : 'Download PNG'}
                            </button>
                            <label className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-gray-500 hover:border-red-400 hover:text-red-500 cursor-pointer transition-colors">
                                <Upload size={12} /> {vn ? 'Ảnh tùy chỉnh' : 'Upload image'}
                                <input ref={customImgRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleCustomImage} />
                            </label>
                            {youtubeVideoId && (
                                <button onClick={handlePushToYouTube}
                                    disabled={isUploading || !ytConnected || !thumbPreview}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-bold bg-red-500 hover:bg-red-600 text-white rounded-lg disabled:opacity-40 transition-colors">
                                    {isUploading ? <Loader2 size={12} className="animate-spin" /> : <Youtube size={12} />}
                                    {vn ? 'Cập nhật YouTube' : 'Push to YouTube'}
                                </button>
                            )}
                            {thumbMsg && (
                                <span className={`self-center text-xs font-medium ${thumbMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>
                                    {thumbMsg}
                                </span>
                            )}
                        </div>
                        {onSaveDraft && (
                            <div className="flex flex-wrap items-center gap-2 -mt-1">
                                <button onClick={handleSave} disabled={!thumbPreview}
                                    title={vn ? 'Áp dụng bản chỉnh này ở mọi nơi hiện thumbnail trong app, và lưu thành nháp để so sánh sau' : 'Apply this version everywhere the app shows this thumbnail, and save it as a draft to compare later'}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-gray-300 hover:border-indigo-400 hover:text-indigo-600 disabled:opacity-40 transition-colors">
                                    <Save size={12} /> {vn ? 'Lưu' : 'Save'}
                                </button>
                                <button onClick={() => { setCardMsg(''); setShowCompare(true); }}
                                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-gray-200 dark:border-slate-600 rounded-lg text-gray-600 dark:text-gray-300 hover:border-indigo-400 hover:text-indigo-600 transition-colors">
                                    <Columns2 size={12} /> {vn ? `So sánh (${(drafts?.length ?? 0) + (liveThumbUrl ? 1 : 0)})` : `Compare (${(drafts?.length ?? 0) + (liveThumbUrl ? 1 : 0)})`}
                                </button>
                                {draftMsg && <span className={`text-xs font-medium ${draftMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{draftMsg}</span>}
                            </div>
                        )}
                        {startedFromRef.current && (
                            <p className="text-[11px] text-gray-400 -mt-1">
                                {vn ? 'Đang bắt đầu từ cài đặt của thumbnail đã push lúc ' : 'Started from the settings of the thumbnail pushed at '}
                                <span className="font-semibold text-gray-500 dark:text-gray-300">{new Date(startedFromRef.current).toLocaleString(vn ? 'vi-VN' : undefined)}</span>
                                {vn ? ' — bấm Reset để về mặc định.' : ' — press Reset for defaults.'}
                            </p>
                        )}

                        {/* Note image multi-select picker */}
                        {noteImages.length > 0 && (
                            <div>
                                <div className="flex items-center justify-between mb-2">
                                    <p className="text-[10px] font-bold text-gray-400 dark:text-slate-500 uppercase tracking-wider">
                                        {vn ? 'Ảnh từ note' : 'Note images'}
                                    </p>
                                    <div className="flex items-center gap-2">
                                        {selectedIdxs.size > 0 && (
                                            <span className="text-[10px] text-gray-400">
                                                {vn ? `${selectedIdxs.size} đã chọn` : `${selectedIdxs.size} selected`}
                                                {selectedIdxs.size >= 2 && (
                                                    <span className="text-amber-500 ml-1">· scattered</span>
                                                )}
                                            </span>
                                        )}
                                        {selectedIdxs.size > 0 && (
                                            <button onClick={() => setSelectedIdxs(new Set())}
                                                className="text-[10px] text-gray-400 hover:text-red-500 transition-colors">
                                                {vn ? 'Bỏ chọn' : 'None'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-4 gap-2">
                                    {noteImages.map((img, idx) => {
                                        const isSelected = selectedIdxs.has(idx);
                                        const orderNum = isSelected ? orderedIdxs.indexOf(idx) + 1 : null;
                                        return (
                                            <button
                                                key={idx}
                                                onClick={() => toggleImage(idx)}
                                                className={`relative rounded-lg overflow-hidden border-2 transition-all bg-gray-100 dark:bg-slate-800 ${
                                                    isSelected
                                                        ? 'border-amber-400 ring-2 ring-amber-200 dark:ring-amber-900/50'
                                                        : 'border-transparent hover:border-gray-300 dark:hover:border-slate-500'
                                                }`}
                                                style={{ aspectRatio: '16/9' }}
                                                title={img.label}
                                            >
                                                <img src={img.src} alt={img.label} className="w-full h-full object-cover" />
                                                {isSelected && orderNum != null && (
                                                    <div className="absolute top-1 right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center text-white text-[9px] font-bold shadow">
                                                        {orderNum}
                                                    </div>
                                                )}
                                                <span className="absolute bottom-0 left-0 right-0 text-[9px] font-semibold text-white bg-black/50 px-1 py-0.5 text-center truncate">
                                                    {img.label}
                                                </span>
                                            </button>
                                        );
                                    })}
                                </div>
                                {selectedIdxs.size >= 4 && (
                                    <p className="text-[10px] text-amber-500 mt-1">{vn ? 'Tối đa 4 ảnh' : 'Max 4 images'}</p>
                                )}
                                {selectedIdxs.size === 1 && (
                                    <label className="flex items-center gap-1.5 mt-2 text-[10px] text-gray-500 dark:text-gray-400 cursor-pointer">
                                        <input type="checkbox" checked={fakeStack} onChange={e => setFakeStack(e.target.checked)} className="accent-blue-500" />
                                        {vn ? 'Giả lập chồng ảnh (xoay lệch cho đẹp dù chỉ có 1 ảnh)' : 'Fake a screenshot stack (rotated/offset, even with 1 image)'}
                                    </label>
                                )}
                            </div>
                        )}

                        {/* Image crop / focal point — applies to single-screenshot and book-cover framing */}
                        {hasImageForCrop && (
                            <div className="border-t border-gray-100 dark:border-slate-700 pt-3">
                                <div className="flex items-center justify-between mb-1.5">
                                    <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">{vn ? 'Khung ảnh' : 'Image crop'}</p>
                                    <div className="flex gap-1">
                                        {(['fit', 'fill'] as ImageFit[]).map(f => (
                                            <button key={f} onClick={() => setImageFit(f)}
                                                className={`px-2 py-0.5 text-[10px] font-bold rounded-md border transition-colors ${imageFit === f ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                                                {f === 'fit' ? (vn ? 'Vừa khung' : 'Fit') : (vn ? 'Lấp đầy' : 'Fill')}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                {/* Kéo chuột trực tiếp lên ảnh để chọn điểm canh giữa — thay cho lưới 9 điểm cố định,
                                    chính xác hơn hẳn khi cần canh đúng vào 1 công thức nhỏ. */}
                                {focalPreviewUrl && (
                                    focalPreviewRatio ? (
                                        <div
                                            ref={focalPreviewRef}
                                            onPointerDown={startFocalDrag}
                                            className="relative rounded-lg overflow-hidden border border-gray-200 dark:border-slate-600 cursor-crosshair select-none"
                                            style={{ width: 160, aspectRatio: String(focalPreviewRatio) }}
                                        >
                                            <img src={focalPreviewUrl} draggable={false} alt=""
                                                className="absolute inset-0 w-full h-full pointer-events-none" style={{ objectFit: 'fill' }} />
                                            <div className="absolute w-4 h-4 rounded-full bg-blue-500 border-2 border-white shadow pointer-events-none"
                                                style={{ left: `${focalDisplayX * 100}%`, top: `${focalDisplayY * 100}%`, transform: 'translate(-50%, -50%)' }} />
                                        </div>
                                    ) : (
                                        <div className="w-40 h-24 flex items-center justify-center rounded-lg border border-gray-200 dark:border-slate-600">
                                            <Loader2 size={14} className="animate-spin text-gray-300" />
                                        </div>
                                    )
                                )}
                                {/* Always shown (never silently disappears) — only ever disabled, with an
                                    explanation, since it has no effect in "Vừa khung" (that mode shows the
                                    whole image, uncropped, so there's nothing to zoom into). */}
                                <div className="mt-2">
                                    <div className="flex items-center justify-between">
                                        <p className={`text-[9px] font-semibold ${imageFit === 'fill' ? 'text-gray-400' : 'text-gray-300 dark:text-slate-600'}`}>{vn ? 'Zoom' : 'Zoom'}</p>
                                        <p className={`text-[9px] ${imageFit === 'fill' ? 'text-gray-400' : 'text-gray-300 dark:text-slate-600'}`}>{Math.round(imageZoom * 100)}%</p>
                                    </div>
                                    <input type="range" min={0} max={1} step={0.05} value={imageZoom} disabled={imageFit !== 'fill'}
                                        onChange={e => setImageZoom(parseFloat(e.target.value))}
                                        className="w-24 accent-blue-500 disabled:opacity-30" />
                                    {imageFit !== 'fill' && (
                                        <p className="text-[9px] text-gray-400 mt-0.5">{vn ? 'Chỉ áp dụng ở chế độ "Lấp đầy"' : 'Only applies in "Fill" mode'}</p>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Right panel: controls */}
                    <div className="w-[280px] border-l border-gray-100 dark:border-slate-800 p-4 overflow-y-auto hide-scrollbar flex-shrink-0 flex flex-col gap-4">

                        {/* Templates — one click sets a coherent bundle of style settings (layout,
                            text length, blur/darken, badge); the Style dropdown + everything below
                            it still lets you fine-tune afterward. */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Mẫu' : 'Template'}</label>
                            <div className="grid grid-cols-3 gap-1.5">
                                <button onClick={applyTopBottomTemplate} title={vn ? 'Trên dưới — ảnh nền mờ full-bleed phía trên, chữ 2 dòng phía dưới. Kéo zoom bên dưới để lấy hết ảnh hoặc cận vào 1 công thức.' : 'Top-bottom — full-bleed blurred image on top, 2-line title below. Use the Zoom control below to show the whole image or zoom into one formula.'}
                                    className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border transition-colors ${activeTemplateId === 'topbottom' ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'}`}>
                                    <span className="text-base leading-none">🔲</span>
                                    <span className={`text-[9px] font-semibold text-center leading-tight ${activeTemplateId === 'topbottom' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>{vn ? 'Trên dưới' : 'Top-Bottom'}</span>
                                </button>
                                <button onClick={applySplitTemplate} title={vn ? 'Chia đôi — screenshot 1 bên, câu hỏi + badge sách bên kia' : 'Split Layout — screenshots on one side, question + book badge on the other'}
                                    className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border transition-colors ${activeTemplateId === 'split' ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'}`}>
                                    <Columns2 size={16} className={activeTemplateId === 'split' ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'} />
                                    <span className={`text-[9px] font-semibold text-center leading-tight ${activeTemplateId === 'split' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>{vn ? 'Chia đôi' : 'Split Layout'}</span>
                                </button>
                                <button onClick={applyTitleTemplate} title={vn ? 'Chỉ tiêu đề — không cần ảnh chụp: tiêu đề trong một nhãn trên nền mờ (bìa sách làm mờ nếu có).' : 'Title only — no screenshot needed: the title in a label over a blurred backdrop (the book cover, when there is one).'}
                                    className={`flex flex-col items-center gap-1 px-1.5 py-2 rounded-lg border transition-colors ${activeTemplateId === 'title' ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-slate-600 hover:border-red-400 hover:bg-red-50 dark:hover:bg-red-900/20'}`}>
                                    <AlignLeft size={16} className={activeTemplateId === 'title' ? 'text-red-500' : 'text-gray-500 dark:text-gray-400'} />
                                    <span className={`text-[9px] font-semibold text-center leading-tight ${activeTemplateId === 'title' ? 'text-red-600 dark:text-red-400' : 'text-gray-600 dark:text-gray-300'}`}>{vn ? 'Chỉ tiêu đề' : 'Title only'}</span>
                                </button>
                            </div>
                        </div>

                        {/* Style + Reset */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">{vn ? 'Kiểu' : 'Style'}</label>
                                <button onClick={handleResetToPreset} title={vn ? 'Khôi phục mặc định preset' : 'Reset to preset defaults'}
                                    className="flex items-center gap-1 text-[10px] font-semibold text-gray-400 hover:text-red-500 transition-colors">
                                    <RotateCcw size={10} /> {vn ? 'Reset' : 'Reset'}
                                </button>
                            </div>
                            <select value={thumbStyle} onChange={e => setThumbStyle(e.target.value as ThumbStyle)}
                                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300">
                                <option value="split">{vn ? 'Chia đôi (ảnh + panel)' : 'Split (image + panel)'}</option>
                                <option value="screenshot">{vn ? 'Ảnh nền + chữ overlay' : 'Screenshot overlay'}</option>
                                <option value="title">{vn ? 'Chỉ tiêu đề (không cần ảnh)' : 'Title only (no image)'}</option>
                            </select>
                        </div>

                        {/* Saved color combos — bg + accent + left panel + title/subtitle text color,
                            reusable across any note/notebook once saved. */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Combo màu đã lưu' : 'Saved color combos'}</label>
                            <div className="flex items-center gap-1.5 flex-wrap">
                                {savedCombos.map(combo => (
                                    <button key={combo.id} onClick={() => handleApplyCombo(combo)}
                                        title={vn ? 'Nhấn để áp dụng · giữ để xóa' : 'Click to apply · hover for delete'}
                                        className="relative w-7 h-7 rounded-full border-2 border-transparent hover:border-white hover:scale-110 transition-transform overflow-hidden group/combo"
                                        style={{ background: `linear-gradient(135deg, ${combo.bg} 50%, ${combo.titleColor} 50%)` }}>
                                        <span
                                            onClick={e => { e.stopPropagation(); handleDeleteCombo(combo.id); }}
                                            className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-gray-700 text-white text-[8px] leading-[14px] opacity-0 group-hover/combo:opacity-100 transition-opacity"
                                        >×</span>
                                    </button>
                                ))}
                                <button onClick={handleSaveCombo} title={vn ? 'Lưu combo màu hiện tại' : 'Save current color combo'}
                                    className="w-7 h-7 rounded-full border-2 border-dashed border-gray-300 dark:border-slate-600 text-gray-400 hover:border-red-400 hover:text-red-500 flex items-center justify-center text-sm transition-colors">
                                    +
                                </button>
                            </div>
                        </div>

                        {/* Preset colors + accent */}
                        {thumbStyle === 'split' && (
                            <div>
                                <label className="flex items-center gap-1.5 mb-2 text-[10px] font-semibold text-gray-500 dark:text-gray-400 cursor-pointer">
                                    <input type="checkbox" checked={splitColorLinked} className="accent-blue-500" onChange={e => {
                                        const linked = e.target.checked;
                                        setSplitColorLinked(linked);
                                        if (linked) {
                                            // Sync to whichever side already has a real flat color, so turning
                                            // this on doesn't silently blank out a choice already made.
                                            const shared = customColor ?? leftBgColor ?? THUMB_PRESETS.find(p => p.id === thumbPreset)?.from ?? '#0f172a';
                                            setCustomColor(shared);
                                            setLeftBgColor(shared);
                                        }
                                    }} />
                                    {vn ? 'Dùng chung 1 màu cho cả 2 bên' : 'Use one color for both sides'}
                                </label>

                                {splitColorLinked ? (
                                    <>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Màu (cả 2 bên)' : 'Color (both sides)'}</label>
                                        <ColorPaletteSwatchPicker value={customColor} vn={vn}
                                            onChange={c => { setCustomColor(c); setLeftBgColor(c); }}
                                            noneTitle={vn ? 'Về màu mặc định riêng từng bên' : "Back to each side's own default"} />
                                    </>
                                ) : (
                                    <>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Màu preset' : 'Color preset'}</label>
                                        <div className="flex gap-1.5 flex-wrap mb-2">
                                            {THUMB_PRESETS.map(p => (
                                                <button key={p.id} onClick={() => { setThumbPreset(p.id); setCustomColor(null); }} title={p.label}
                                                    className={`w-6 h-6 rounded-full border-2 transition-transform ${!customColor && thumbPreset === p.id ? 'border-white scale-125 shadow-md' : 'border-transparent hover:scale-110'}`}
                                                    style={{ background: `linear-gradient(135deg, ${p.from}, ${p.to})` }} />
                                            ))}
                                            <button onClick={() => colorPickerRef.current?.click()} title={vn ? 'Màu tùy chỉnh' : 'Custom'}
                                                className={`w-6 h-6 rounded-full border-2 transition-transform overflow-hidden hover:scale-110 ${customColor ? 'border-white scale-125 shadow-md' : 'border-transparent'}`}
                                                style={{ background: customColor ?? 'conic-gradient(red,yellow,lime,aqua,blue,magenta,red)' }} />
                                            <input ref={colorPickerRef} type="color" className="hidden" value={customColor ?? '#92400e'} onChange={e => setCustomColor(e.target.value)} />
                                        </div>
                                        {/* Flat (non-gradient) alternative to the 6 gradient presets above — picking
                                            one here sets customColor directly, which already makes the right panel a
                                            solid fill instead of a gradient (bgFrom/bgTo both resolve to it). */}
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-2">{vn ? 'Hoặc màu phẳng (flat)' : 'Or a flat color'}</label>
                                        <ColorPaletteSwatchPicker value={customColor} onChange={setCustomColor} vn={vn}
                                            noneTitle={vn ? 'Dùng gradient preset ở trên' : 'Use the gradient preset above'} />
                                    </>
                                )}

                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-3">{vn ? 'Màu nhấn (đường kẻ dọc)' : 'Accent (divider line)'}</label>
                                <div className="flex items-center gap-2">
                                    <button onClick={() => accentColorPickerRef.current?.click()}
                                        className={`w-8 h-8 rounded-lg border-2 flex-shrink-0 ${accentColor !== 'transparent' ? 'border-blue-400' : 'border-gray-200 dark:border-slate-600'}`}
                                        style={{ background: accentColor && accentColor !== 'transparent' ? accentColor : DEFAULT_ACCENT }} />
                                    <input ref={accentColorPickerRef} type="color" className="hidden" value={accentColor && accentColor !== 'transparent' ? accentColor : DEFAULT_ACCENT} onChange={e => setAccentColor(e.target.value)} />
                                    <button onClick={() => setAccentColor('transparent')} title={vn ? 'Ẩn đường kẻ' : 'Hide the line'}
                                        className={`w-8 h-8 rounded-lg border-2 flex-shrink-0 bg-[length:8px_8px] ${accentColor === 'transparent' ? 'border-blue-400' : 'border-gray-200 dark:border-slate-600'}`}
                                        style={{ backgroundImage: 'repeating-conic-gradient(#d1d5db 0% 25%, #f3f4f6 0% 50%)', backgroundSize: '8px 8px' }} />
                                    <div className="flex gap-1">
                                        {[2, 4, 8].map(w => (
                                            <button key={w} onClick={() => setAccentWidth(w)}
                                                className={`px-2 py-1 text-[10px] font-bold rounded-md border transition-colors ${accentWidth === w ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                                                {w === 2 ? (vn ? 'Mảnh' : 'Thin') : w === 4 ? (vn ? 'Vừa' : 'Mid') : (vn ? 'Dày' : 'Thick')}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                {/* Hidden while linked — the shared picker above already drives this. */}
                                {selectedIdxs.size > 0 && !splitColorLinked && (
                                    <>
                                        <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1 mt-3">{vn ? 'Màu nền trái (sau ảnh)' : 'Left panel background'}</label>
                                        <ColorPaletteSwatchPicker value={leftBgColor} onChange={setLeftBgColor} vn={vn}
                                            noneTitle={vn ? 'Mặc định (ảnh lấp đầy)' : 'Default (image fills panel)'} />
                                    </>
                                )}
                            </div>
                        )}

                        {/* Background — screenshot ("Trên dưới") style only: blur the screenshot itself
                            (default) or replace it with a solid/custom color. */}
                        {isOverlayStyle(thumbStyle) && (
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Nền' : 'Background'}</label>
                                <ColorPaletteSwatchPicker value={bgSolidColor} onChange={setBgSolidColor} vn={vn}
                                    noneTitle={vn ? 'Blur ảnh nền (mặc định)' : 'Blur the screenshot (default)'} />
                            </div>
                        )}

                        {/* Title */}
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">{vn ? 'Tiêu đề' : 'Title'}</label>
                                <button onClick={handleGenTitle} disabled={isGenTitle || !noteContent}
                                    title={vn ? 'Đọc note và gợi ý tiêu đề' : 'Read the note and suggest a title'}
                                    className="flex items-center gap-1 text-[10px] font-semibold text-red-500 hover:text-red-600 disabled:opacity-40 transition-colors">
                                    {isGenTitle ? <Loader2 size={10} className="animate-spin" /> : <Sparkles size={10} />}
                                    {vn ? 'AI tạo' : 'AI generate'}
                                </button>
                            </div>
                            <textarea value={thumbTitle} onChange={e => setThumbTitle(e.target.value)} rows={4} maxLength={100}
                                placeholder={title}
                                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-2 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300 resize-none" />
                        </div>

                        {/* Font family */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Phông chữ' : 'Font'}</label>
                            <div className="flex gap-1.5 flex-wrap">
                                {THUMB_FONTS.map(f => (
                                    <button key={f.id} onClick={() => setTitleFontId(f.id)}
                                        style={{ fontFamily: f.family }}
                                        className={`px-2.5 py-1 text-xs font-bold rounded-lg border transition-colors ${titleFontId === f.id ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-500 hover:border-gray-400'}`}>
                                        {f.label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Title font size */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Cỡ chữ tiêu đề' : 'Title size'}</label>
                            <ScaleSlider value={titleScale} onChange={setTitleScale} />
                        </div>

                        {/* Title alignment (screenshot mode only — split's left-anchor is fixed by its layout) */}
                        {isOverlayStyle(thumbStyle) && (
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Căn chữ' : 'Alignment'}</label>
                                <div className="flex gap-1.5">
                                    {([['left', AlignLeft], ['center', AlignCenter], ['right', AlignRight]] as [TitleAlign, React.ElementType][]).map(([a, Icon]) => (
                                        <button key={a} onClick={() => setTitleAlign(a)}
                                            className={`flex-1 flex items-center justify-center py-1.5 rounded-lg border transition-colors ${titleAlign === a ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                                            <Icon size={13} />
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Title placement — both modes */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Vị trí chữ' : 'Title placement'}</label>
                            <div className="flex gap-1.5">
                                {([['top', ArrowUp, vn ? 'Trên' : 'Top'], ['center', AlignCenter, vn ? 'Giữa' : 'Mid'], ['bottom', ArrowDown, vn ? 'Dưới' : 'Bot']] as [TitlePlacement, React.ElementType, string][]).map(([pos, Icon, label]) => (
                                    <button key={pos} onClick={() => setPlacement(pos)}
                                        className={`flex-1 flex flex-col items-center gap-0.5 py-1.5 text-[10px] font-bold rounded-lg border transition-colors ${placement === pos ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                                        <Icon size={12} />{label}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Text Effect: Auto | None | Custom */}
                        <div className="border-t border-gray-100 dark:border-slate-700 pt-4">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Hiệu ứng chữ' : 'Text Effect'}</label>
                            <div className="flex gap-1.5 mb-2">
                                {(['auto', 'none', 'custom'] as TextEffectMode[]).map(m => (
                                    <button key={m} onClick={() => setTextEffectMode(m)}
                                        className={`flex-1 py-1.5 text-[11px] font-bold rounded-lg border transition-colors capitalize ${textEffectMode === m ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                                        {m === 'auto' ? (vn ? 'Tự động' : 'Auto') : m === 'none' ? (vn ? 'Tắt' : 'None') : (vn ? 'Tùy chỉnh' : 'Custom')}
                                    </button>
                                ))}
                            </div>
                            {textEffectMode === 'auto' && (
                                <p className="text-[10px] text-gray-400 leading-snug">{vn ? 'Tự tính bóng/viền theo màu chữ & nền để luôn dễ đọc.' : 'Shadow/outline computed from text & background color for readability.'}</p>
                            )}
                            {textEffectMode === 'custom' && (
                                <div className="space-y-3">
                                    <div>
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                                            <input type="checkbox" checked={customShadowEnabled} onChange={e => setCustomShadowEnabled(e.target.checked)} className="accent-red-500" />
                                            {vn ? 'Bóng đổ' : 'Shadow'}
                                        </label>
                                        {customShadowEnabled && (
                                            <div className="space-y-1.5 pl-1">
                                                <ColorInput value={customShadow.color} onChange={c => setCustomShadow({ ...customShadow, color: c })} />
                                                <MiniSlider value={customShadow.opacity} min={0} max={1} step={0.05} onChange={n => setCustomShadow({ ...customShadow, opacity: n })} fmt={n => `${Math.round(n * 100)}%`} />
                                                <MiniSlider value={customShadow.blur} min={0} max={40} step={1} onChange={n => setCustomShadow({ ...customShadow, blur: n })} fmt={n => `${n}px`} />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">
                                            <input type="checkbox" checked={customOutlineEnabled} onChange={e => setCustomOutlineEnabled(e.target.checked)} className="accent-red-500" />
                                            {vn ? 'Viền chữ' : 'Outline'}
                                        </label>
                                        {customOutlineEnabled && (
                                            <div className="space-y-1.5 pl-1">
                                                <ColorInput value={customOutline.color} onChange={c => setCustomOutline({ ...customOutline, color: c })} />
                                                <MiniSlider value={customOutline.width} min={0} max={8} step={0.5} onChange={n => setCustomOutline({ ...customOutline, width: n })} fmt={n => `${n}px`} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Title color */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Màu chữ tiêu đề' : 'Title color'}</label>
                            <ColorInput value={titleColor} onChange={setTitleColor} />
                        </div>

                        {/* Tagline */}
                        <div>
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Tagline (dòng phụ)' : 'Tagline'}</label>
                            <input value={thumbTagline} onChange={e => setThumbTagline(e.target.value)} maxLength={70}
                                placeholder={vn ? 'VD: Sai lầm phổ biến #3' : 'e.g. Common mistake #3'}
                                className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300" />
                        </div>

                        {/* Subtitle controls */}
                        <div className="border-t border-gray-100 dark:border-slate-700 pt-4 space-y-3">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider">{vn ? 'Tên sách / subtitle' : 'Book name / subtitle'}</label>
                            <div>
                                <label className="block text-[10px] text-gray-400 mb-1">{vn ? 'Nội dung' : 'Text'}</label>
                                <input type="text" value={thumbSubtitleText} onChange={e => setThumbSubtitleText(e.target.value)}
                                    placeholder={notebookTitle}
                                    className="w-full text-sm border border-gray-200 dark:border-slate-600 rounded-lg px-3 py-1.5 bg-white dark:bg-slate-800 text-gray-800 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-red-300" />
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-400 mb-1">{vn ? 'Cỡ chữ' : 'Size'}</label>
                                <ScaleSlider value={thumbSubtitleScale} onChange={setThumbSubtitleScale} />
                            </div>
                            <div>
                                <label className="block text-[10px] text-gray-400 mb-1">{vn ? 'Màu' : 'Color'}</label>
                                <ColorInput value={thumbSubtitleColor} onChange={setThumbSubtitleColor} />
                            </div>
                        </div>

                        {/* Blur + darkness controls (screenshot style only) */}
                        {isOverlayStyle(thumbStyle) && (
                            <div className="space-y-3">
                                {/* Only meaningful for the blurred-screenshot background — always shown
                                    (never silently disappears), just disabled + explained when a solid
                                    color is picked instead, same fix as the Zoom control got. */}
                                <div className={bgSolidColor ? 'opacity-40 pointer-events-none' : undefined}>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{vn ? 'Độ blur nền' : 'Background blur'}</label>
                                    <MiniSlider value={thumbBgBlur} min={0} max={30} step={1} onChange={setThumbBgBlur} fmt={n => `${n}px`} />
                                </div>
                                <div className={bgSolidColor ? 'opacity-40 pointer-events-none' : undefined}>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{vn ? 'Độ tối nền' : 'Background darkness'}</label>
                                    <MiniSlider value={thumbBgDark} min={0} max={0.9} step={0.05} onChange={setThumbBgDark} fmt={n => `${Math.round(n * 100)}%`} />
                                </div>
                                {bgSolidColor && (
                                    <p className="text-[9px] text-gray-400 -mt-2">{vn ? 'Không áp dụng khi dùng màu nền solid' : "Doesn't apply when using a solid background color"}</p>
                                )}
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1">{vn ? 'Độ tối vùng chữ' : 'Text strip darkness'}</label>
                                    <MiniSlider value={thumbTextStripDark} min={0} max={1} step={0.05} onChange={setThumbTextStripDark} fmt={n => `${Math.round(n * 100)}%`} />
                                </div>
                            </div>
                        )}

                        {/* Corner radius */}
                        {hasImageForCrop && (
                            <div>
                                <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">{vn ? 'Bo góc ảnh' : 'Image corners'}</label>
                                <div className="flex gap-1.5">
                                    {(['none', 'small', 'medium', 'large'] as CornerRadiusSize[]).map(c => (
                                        <button key={c} onClick={() => setCornerRadius(c)}
                                            className={`flex-1 py-1.5 text-[10px] font-bold rounded-lg border transition-colors capitalize ${cornerRadius === c ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300' : 'border-gray-200 dark:border-slate-600 text-gray-400 hover:border-gray-400'}`}>
                                            {c === 'none' ? (vn ? 'Không' : 'None') : c === 'small' ? (vn ? 'Nhỏ' : 'Small') : c === 'medium' ? (vn ? 'Vừa' : 'Med') : (vn ? 'Lớn' : 'Large')}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Watermark */}
                        <div className="border-t border-gray-100 dark:border-slate-700 pt-4">
                            <label className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-1.5">
                                <input type="checkbox" checked={watermarkEnabled} onChange={e => setWatermarkEnabled(e.target.checked)} className="accent-red-500" />
                                {vn ? 'Watermark (studyboard.app)' : 'Watermark (studyboard.app)'}
                            </label>
                            {watermarkEnabled && (
                                <MiniSlider value={watermarkOpacity} min={0.1} max={1} step={0.05} onChange={setWatermarkOpacity} fmt={n => `${Math.round(n * 100)}%`} />
                            )}
                        </div>

                        {/* Book cover — shown for both styles */}
                        <div className="border-t border-gray-100 dark:border-slate-700 pt-4">
                            <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-2">{vn ? 'Bìa sách' : 'Book cover'}</label>
                            {coverDataUrl ? (
                                <div className="flex items-center gap-2 px-2 py-1.5 bg-gray-50 dark:bg-slate-800 rounded-lg">
                                    <img src={coverDataUrl} className="w-8 h-10 object-cover rounded" alt="cover" />
                                    <span className="text-[11px] text-gray-500 dark:text-gray-400 flex-1">{vn ? 'Bìa đã chọn' : 'Cover selected'}</span>
                                    <button onClick={() => saveCover(null)} className="text-[11px] text-gray-400 hover:text-red-500 transition-colors">✕</button>
                                </div>
                            ) : (
                                <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-[11px] text-gray-400 hover:border-amber-400 hover:text-amber-600 cursor-pointer transition-colors">
                                    <ImageIcon size={12} /> {vn ? '+ Thêm bìa sách' : '+ Add book cover'}
                                    <input ref={coverInputRef} type="file" accept="image/*" className="hidden"
                                        onChange={e => {
                                            const f = e.target.files?.[0]; if (!f) return;
                                            const reader = new FileReader();
                                            reader.onload = ev => saveCover(ev.target?.result as string);
                                            reader.readAsDataURL(f); e.target.value = '';
                                        }} />
                                </label>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Compare: what is live on YouTube next to every saved draft */}
            {showCompare && (
                <div className="fixed inset-0 z-[600] bg-black/70 flex items-center justify-center p-4" onClick={e => { e.stopPropagation(); setShowCompare(false); }}>
                    <div className="w-[980px] max-w-full max-h-[90vh] bg-white dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="flex items-center gap-3 px-5 py-3.5 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
                            <h3 className="font-bold text-sm text-gray-900 dark:text-white flex-1">{vn ? 'So sánh các bản thumbnail' : 'Compare thumbnail versions'}</h3>
                            {cardMsg && <span className={`text-xs font-medium ${cardMsg.startsWith('✓') ? 'text-green-600 dark:text-green-400' : 'text-red-500'}`}>{cardMsg}</span>}
                            <button onClick={() => setShowCompare(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg"><X size={14} className="text-gray-400" /></button>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {liveThumbUrl && (
                                <div className="rounded-xl border-2 border-green-400 dark:border-green-700 overflow-hidden flex flex-col">
                                    <img src={liveThumbUrl} alt="" className="w-full aspect-video object-cover bg-gray-100 dark:bg-slate-800" />
                                    <div className="p-3 space-y-2">
                                        <p className="text-xs font-bold text-green-700 dark:text-green-400">{vn ? 'Đang dùng trên YouTube' : 'Live on YouTube'}</p>
                                        {settingsSavedAt && <p className="text-[10px] text-gray-400">{vn ? 'Push lúc' : 'Pushed'} {new Date(settingsSavedAt).toLocaleString(vn ? 'vi-VN' : undefined)}</p>}
                                        {initialSettings && Object.keys(initialSettings).length > 5 && (
                                            <button onClick={() => { applySettingsToState(initialSettings as ThumbSettings); setShowCompare(false); }}
                                                className="flex items-center gap-1 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"><Pencil size={10} /> {vn ? 'Sửa từ bản này' : 'Edit from this'}</button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {(drafts || []).map(d => (
                                <div key={d.id} className="rounded-xl border border-gray-200 dark:border-slate-700 overflow-hidden flex flex-col">
                                    <img src={d.preview} alt={d.name} className="w-full aspect-video object-cover bg-gray-100 dark:bg-slate-800" />
                                    <div className="p-3 space-y-2">
                                        <div className="flex items-center gap-2">
                                            <input defaultValue={d.name} maxLength={30}
                                                onBlur={e => { const n = e.target.value.trim(); if (n && n !== d.name) onSaveDraft?.({ ...d, name: n }); }}
                                                className="flex-1 min-w-0 text-xs font-bold bg-transparent border-b border-transparent hover:border-gray-300 focus:border-indigo-400 outline-none text-gray-800 dark:text-gray-100" />
                                            <span className="text-[10px] text-gray-400 flex-shrink-0">{new Date(d.createdAt).toLocaleDateString(vn ? 'vi-VN' : undefined)}</span>
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            <button onClick={() => { applySettingsToState(d.settings); setShowCompare(false); }}
                                                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600"><Pencil size={10} /> {vn ? 'Sửa' : 'Edit'}</button>
                                            <button onClick={() => pushSettings(d.id, d.settings)} disabled={busyCardId === d.id || !youtubeVideoId || !ytConnected}
                                                title={vn ? 'Đặt làm thumbnail đang dùng (tốn ~50 đơn vị quota)' : 'Make this the live thumbnail (~50 quota units)'}
                                                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md bg-red-500 hover:bg-red-600 text-white disabled:opacity-40">
                                                {busyCardId === d.id ? <Loader2 size={10} className="animate-spin" /> : <Youtube size={10} />} {vn ? 'Dùng bản này' : 'Use this'}
                                            </button>
                                            <button onClick={() => downloadSettings(d.id, d.settings, d.name)} disabled={busyCardId === d.id}
                                                title={vn ? 'Tải PNG để dùng trong Thử nghiệm A/B của YouTube Studio' : 'Download PNG for YouTube Studio Test & Compare'}
                                                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold rounded-md bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-slate-600 disabled:opacity-40"><Download size={10} /> PNG</button>
                                            <button onClick={() => onDeleteDraft?.(d.id)}
                                                className="ml-auto p-1 text-gray-400 hover:text-red-500" title={vn ? 'Xóa nháp' : 'Delete draft'}><Trash2 size={12} /></button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {!liveThumbUrl && (drafts || []).length === 0 && (
                                <p className="col-span-full text-sm text-gray-400 text-center py-10">{vn ? 'Chưa có bản nháp nào — bấm "Lưu thành nháp" trong editor.' : 'No drafts yet — press "Save as draft" in the editor.'}</p>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
